import React, { useState, useEffect } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Helmet } from "react-helmet";
import SimulatorRenopack from "@/components/SimulatorRenopack";
import { getUtmParams, setCookieConsent } from "@/lib/attribution";
import { PrivacyContent } from "@/components/PrivacyContent";
import { AdsConsentCheckbox } from "@/components/AdsConsentCheckbox";
import AdminLeads from "@/pages/AdminLeads";
import { usePageTracking } from "@/lib/pageTracking";
import { 
  Phone, ClipboardCheck, Home, ShieldCheck, CheckCircle2, ArrowRight,
  Clock, Users, ChevronRight, ChevronLeft, MapPin, FileCheck, HelpCircle,
  Banknote, Thermometer, Wind, Sun, Droplets, Zap, Building2, Leaf, Check, Star,
  Calendar, AlertCircle, Info, BadgeCheck, FileSearch,
  Building, Flame, Shield, FileText, Euro, Cookie, Wallet, TrendingUp, ArrowUp, 
  Wrench, PiggyBank, DoorOpen, Lightbulb, Target, Award, Timer, CircleDollarSign, Landmark, Menu
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

// ============ UTILS IMPORTS (for future use) ============
// Note: Most constants and functions are still defined locally in App.js
// Future refactoring will gradually migrate to these imports:
// import { API } from './utils/constants';
// import { initUtmCapture } from './utils/tracking';
// import { PRIME_AUDIT_BASE, ... } from './data/pricing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============ LOCAL CONSTANTS ============
const PHONE_DISPLAY = "078 79 01 15";
const PHONE_LINK = "+3278790115";

// Determine form_type based on current page or source
const getFormType = (source) => {
  const path = window.location.pathname;
  if (source === 'peb') return 'peb';
  if (path.includes('peb') || path.includes('certificat')) return 'peb';
  if (path.includes('audit') || path.includes('simulateur') || path.includes('estimation')) return 'audit';
  if (source === 'simulateur' || source === 'estimation' || source === 'form') return 'audit';
  return 'other';
};

// Initialize UTM capture on page load
if (typeof window !== 'undefined') {
  getUtmParams();
}

// GTM tracking helper
const trackEvent = (event, category, action, label, extra = {}) => {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: event,
      event_category: category,
      event_action: action,
      event_label: label,
      ...extra
    });
  }
};

// Backend tracking for dashboard analytics (non-blocking)
const sendBackendTrack = (type, source) => {
  try {
    const url = `${API}/track/click`;
    const utmParams = getUtmParams();
    const body = JSON.stringify({
      type,
      source: source || '',
      page: window.location.pathname,
      user_agent: navigator.userAgent || '',
      keyword: utmParams.keyword || '',
      gclid: utmParams.gclid || '',
      landing_url: utmParams.landing_url || '',
      form_type: getFormType(source),
      ad_user_data_consent: utmParams.ad_user_data_consent,
    });
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
    } else {
      fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
    }
  } catch (e) {}
};

// Track phone click
const trackPhoneClick = (label) => {
  trackEvent('phone_click', 'Lead', 'Phone Click', label, {
    click_url: `tel:${PHONE_LINK}`,
    click_text: PHONE_DISPLAY
  });
  sendBackendTrack('phone', label);
};

// Track CTA click
const trackCTAClick = (label, text = '') => {
  trackEvent('cta_click', 'Engagement', 'CTA Click', label, {
    click_text: text
  });
};

// Track form submit
const trackFormSubmit = (source = 'form', callback = null) => {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'form_submit',
      event_category: 'Lead',
      event_action: 'Form Submit',
      event_label: 'Devis Audit',
      click_text: 'Recevoir mon devis gratuit',
      form_source: source,
      eventCallback: function() {
        if (typeof callback === 'function') callback();
      },
      eventTimeout: 2000
    });
  } else if (typeof callback === 'function') {
    callback();
  }
};

// Images WebP optimisées avec fallback JPG
const LOCAL_IMAGES_WEBP = [
  "/images/audit1.webp", "/images/audit2.webp", "/images/audit3.webp",
  "/images/audit4.webp", "/images/audit5.webp", "/images/audit6.webp",
  "/images/audit7.webp", "/images/audit8.webp", "/images/audit9.webp"
];

const LOCAL_IMAGES_JPG = [
  "/images/audit1.jpg", "/images/audit2.jpg", "/images/audit3.jpg",
  "/images/audit4.jpg", "/images/audit5.jpg", "/images/audit6.jpg",
  "/images/audit7.jpg", "/images/audit8.jpg", "/images/audit9.jpg"
];

// Composant image optimisé avec WebP et lazy loading
const OptimizedImage = ({ index, alt, className, priority = false }) => (
  <picture>
    <source srcSet={LOCAL_IMAGES_WEBP[index]} type="image/webp" />
    <img 
      src={LOCAL_IMAGES_JPG[index]} 
      alt={alt} 
      className={className}
      loading={priority ? "eager" : "lazy"}
      decoding={priority ? "sync" : "async"}
    />
  </picture>
);

// Logo
const Logo = ({ light = false }) => (
  <a href="#top" className="flex items-center gap-2.5">
    <img src="/logo.svg" alt="" className="w-10 h-10" />
    <div className="hidden sm:block">
      <span className={`font-bold text-lg leading-tight block ${light ? 'text-white' : 'text-gray-900'}`}>Audits Logement</span>
      <span className={`text-xs font-semibold tracking-wide ${light ? 'text-green-300' : 'text-green-600'}`}>WALLONIE</span>
    </div>
  </a>
);

// SEO - Helmet pour le head dynamique (complémente index.html)
const SEOHead = () => (
  <Helmet>
    <title>Audit Logement Wallonie 2026 | Rénopack 0% et jusqu'à 50% de réduction | Devis gratuit</title>
    <meta name="description" content="Audit logement en Wallonie, requis pour le Rénopack depuis le 1er octobre 2026 : prêt à taux 0% et jusqu'à 50% de réduction. Devis gratuit en 24h." />
    <link rel="canonical" href="https://audits-logement-wallonie.be/" />
    {/* Preload hero image for better LCP */}
    <link rel="preload" as="image" href="/images/hero-person.webp" type="image/webp" />
    {/* Preload critical font */}
    <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
  </Helmet>
);

// Header with mobile menu
const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  
  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm">
      <nav className="container mx-auto px-3 lg:px-8">
        <div className="flex items-center justify-between h-14 lg:h-16">
          {/* Logo + Title */}
          <a href="/" className="flex items-center gap-1.5 lg:gap-2">
            <img src="/logo.svg" alt="" className="w-7 h-7 lg:w-10 lg:h-10" />
            <div>
              <span className="font-bold text-sm lg:text-lg leading-tight block text-gray-900">Audits Logement</span>
              <span className="text-[10px] lg:text-xs font-semibold tracking-wide text-green-600">WALLONIE</span>
            </div>
          </a>
          
          {/* Navigation Menu - Desktop */}
          <div className="hidden lg:flex items-center gap-6">
            <a href="/" className="text-gray-700 hover:text-green-600 font-medium text-sm transition-colors">Audit Logement</a>
            <a href="/certificat-peb" className="text-gray-700 hover:text-green-600 font-medium text-sm transition-colors">Certificat PEB</a>
            <a href="/simulateur" className="text-gray-700 hover:text-green-600 font-medium text-sm transition-colors">Simulateur Rénopack</a>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Phone icon */}
            <a href={`tel:${PHONE_LINK}`} onClick={() => trackPhoneClick('Header')} className="flex items-center justify-center w-10 h-10 bg-green-50 text-green-700 rounded-full hover:bg-green-100 transition-colors">
              <Phone className="w-5 h-5" />
            </a>
            {/* Mobile menu button */}
            <button 
              onClick={() => setMenuOpen(!menuOpen)} 
              className="lg:hidden flex items-center justify-center w-10 h-10 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
        
        {/* Mobile menu dropdown */}
        {menuOpen && (
          <div className="lg:hidden border-t border-gray-100 py-4 space-y-2">
            <a href="/" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg font-medium">Audit Logement</a>
            <a href="/certificat-peb" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg font-medium">Certificat PEB</a>
            <a href="/simulateur" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg font-medium">Simulateur Rénopack</a>
            <div className="border-t border-gray-100 pt-2 mt-2">
              <p className="px-4 text-xs text-gray-500 mb-2">Sur cette page</p>
              <a href="#renopack-explique" onClick={() => setMenuOpen(false)} className="block px-4 py-2 text-green-600 hover:bg-green-50 rounded-lg text-sm">Le Rénopack en détail</a>
              <a href="#formulaire" onClick={() => setMenuOpen(false)} className="block px-4 py-2 text-green-600 hover:bg-green-50 rounded-lg text-sm">Demander un devis</a>
              <a href="#faq" onClick={() => setMenuOpen(false)} className="block px-4 py-2 text-green-600 hover:bg-green-50 rounded-lg text-sm">Questions fréquentes</a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

// Alert Banner for new regulation
const RegulationBanner = () => (
  <div className="bg-gray-800 text-white py-2 px-4 text-sm">
    <div className="container mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 text-center">
      <div className="flex items-center gap-2">
        <span className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded">NOUVEAU</span>
        <span className="text-gray-300">Depuis le 1er octobre 2026 : <strong className="text-white">prêt à taux 0%</strong> pour vos travaux <strong className="text-yellow-400">avec un audit</strong></span>
      </div>
      <a 
        href="/#renopack-explique"
        className="inline-flex items-center gap-1 text-green-400 hover:text-green-300 font-medium transition-colors"
        onClick={() => trackCTAClick('Banner', 'OnVousExplique')}
      >
        On vous explique
        <ArrowRight className="w-4 h-4" />
      </a>
    </div>
  </div>
);

// Hero Section V1 (Current)
const HeroSectionV1 = () => (
  <section id="top" className="relative overflow-hidden">
    {/* Background with optimized WebP image - priority loading for LCP */}
    <div className="absolute inset-0">
      <picture>
        <source srcSet="/images/hero-person.webp" type="image/webp" />
        <img 
          src="/images/hero-person.jpg" 
          alt="Professionnel réalisant un audit logement en Wallonie" 
          className="w-full h-full object-cover"
          loading="eager"
          decoding="sync"
          fetchPriority="high"
        />
      </picture>
      <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/90 to-gray-900/70" />
    </div>
    
    <div className="container mx-auto px-4 lg:px-8 py-16 lg:py-24 relative">
      <div className="max-w-3xl">
        <div className="flex items-center gap-2 mb-6">
          <BadgeCheck className="w-5 h-5 text-green-400" />
          <span className="text-green-300 font-medium">Auditeurs agréés par la Région Wallonne</span>
        </div>
        
        <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
          Rénovez avec le <span className="text-green-400">Rénopack</span><br/>
          <span className="text-yellow-400">Prêt à taux 0%</span> + jusqu'à <span className="text-yellow-400">50%</span> de réduction
        </h1>
        
        <p className="text-xl text-gray-300 mb-8 leading-relaxed max-w-2xl">
          Votre logement est énergivore ? <strong className="text-white">La Région Wallonne finance vos travaux</strong> avec un prêt sans intérêt.<br/>
          <strong className="text-green-400">L'audit logement est obligatoire</strong> pour en bénéficier — nous vous mettons en relation gratuitement avec un auditeur agréé.
        </p>
        
        <div className="flex flex-wrap gap-4 mb-10">
          <Button asChild size="lg" className="bg-green-500 hover:bg-green-600 text-white h-14 px-8 text-lg shadow-xl shadow-green-500/30">
            <a href="#formulaire" onClick={() => trackCTAClick('Hero', 'Demande Devis')}>
              Demander un devis gratuit
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 h-14 px-6 text-base">
            <a href={`tel:${PHONE_LINK}`} onClick={() => trackPhoneClick('Hero')}>
              <Phone className="w-4 h-4 mr-2" />
              Nous appeler
            </a>
          </Button>
        </div>
        
        <div className="flex flex-wrap gap-6 text-white/80">
          {[
            { icon: CheckCircle2, text: "Service 100% gratuit" },
            { icon: Euro, text: "Prêt taux 0%" },
            { icon: ShieldCheck, text: "Auditeurs agréés" },
            { icon: MapPin, text: "Toute la Wallonie" }
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <item.icon className="w-5 h-5 text-green-400" />
              <span>{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// Comparateur de prêts Rénopack vs Banque classique
const LoanComparator = () => {
  const [montant, setMontant] = useState(40000);
  const [categorie, setCategorie] = useState('C2'); // C2 par défaut (40% réduction)
  
  const DUREE_MOIS = 240; // 20 ans
  const TAUX_BANQUE = 4.5; // Taux bancaire moyen estimé (%)
  const COUT_AUDIT = 995; // Coût audit maison moyenne
  
  const reductions = { C1: 50, C2: 40, C3: 15 };
  const reduction = reductions[categorie];
  
  // Calcul Rénopack (taux 0% + réduction)
  const montantARembourserRenopack = montant * (1 - reduction / 100);
  const mensualiteRenopack = montantARembourserRenopack / DUREE_MOIS;
  const coutTotalRenopack = montantARembourserRenopack + COUT_AUDIT;
  
  // Calcul Banque classique (avec intérêts)
  const tauxMensuel = TAUX_BANQUE / 100 / 12;
  const mensualiteBanque = montant * (tauxMensuel * Math.pow(1 + tauxMensuel, DUREE_MOIS)) / (Math.pow(1 + tauxMensuel, DUREE_MOIS) - 1);
  const coutTotalBanque = mensualiteBanque * DUREE_MOIS;
  const interetsBanque = coutTotalBanque - montant;
  
  // Économies
  const economie = coutTotalBanque - coutTotalRenopack;
  
  const formatMontant = (n) => new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
  
  return (
    <div className="mt-12 pt-10 border-t border-gray-700">
      <div className="text-center mb-8">
        <h3 className="text-xl lg:text-2xl font-bold text-white mb-2">
          Comparez : Rénopack vs Prêt bancaire classique
        </h3>
        <p className="text-gray-400 text-sm">Famille type • Maison moyenne • Durée 20 ans</p>
      </div>
      
      {/* Sliders */}
      <div className="max-w-2xl mx-auto mb-8 space-y-6">
        {/* Montant emprunté */}
        <div className="bg-gray-800 rounded-xl p-4">
          <div className="flex justify-between items-center mb-2">
            <label className="text-gray-300 text-sm font-medium">Montant emprunté</label>
            <span className="text-2xl font-bold text-green-400">{formatMontant(montant)}</span>
          </div>
          <input 
            type="range" 
            min="10000" 
            max="75000" 
            step="5000"
            value={montant}
            onChange={(e) => setMontant(Number(e.target.value))}
            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-green-500"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>10.000€</span>
            <span>75.000€</span>
          </div>
        </div>
        
        {/* Catégorie de revenus */}
        <div className="bg-gray-800 rounded-xl p-4">
          <label className="text-gray-300 text-sm font-medium mb-3 block">Votre catégorie de revenus</label>
          <div className="grid grid-cols-3 gap-2">
            {Object.entries(reductions).map(([cat, red]) => (
              <button
                key={cat}
                onClick={() => setCategorie(cat)}
                className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                  categorie === cat 
                    ? 'bg-green-500 text-white' 
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {cat}
                <span className="block text-xs opacity-75">-{red}%</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Comparaison côte à côte */}
      <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {/* Rénopack */}
        <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-2xl p-6 relative overflow-hidden">
          <div className="absolute top-0 right-0 bg-yellow-400 text-gray-900 text-xs font-bold px-3 py-1 rounded-bl-lg">
            RECOMMANDÉ
          </div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <PiggyBank className="w-5 h-5 text-white" />
            </div>
            <div>
              <h4 className="text-lg font-bold text-white">Rénopack</h4>
              <p className="text-green-200 text-xs">Prêt Région Wallonne</p>
            </div>
          </div>
          
          <div className="space-y-3 mb-4">
            <div className="flex justify-between items-center">
              <span className="text-green-100 text-sm">Taux d'intérêt</span>
              <span className="text-white font-bold text-lg">0%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-green-100 text-sm">Réduction ({categorie})</span>
              <span className="text-yellow-300 font-bold text-lg">-{reduction}%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-green-100 text-sm">Montant à rembourser</span>
              <span className="text-white font-bold">{formatMontant(montantARembourserRenopack)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-green-100 text-sm">Coût audit (obligatoire)</span>
              <span className="text-white">{formatMontant(COUT_AUDIT)}</span>
            </div>
            <div className="border-t border-green-500 pt-3 flex justify-between items-center">
              <span className="text-green-100 text-sm">Mensualité</span>
              <span className="text-white font-bold text-xl">{formatMontant(mensualiteRenopack)}/mois</span>
            </div>
          </div>
          
          <div className="bg-green-800/50 rounded-lg p-3">
            <div className="flex justify-between items-center">
              <span className="text-green-100 font-medium">Coût total</span>
              <span className="text-white font-bold text-2xl">{formatMontant(coutTotalRenopack)}</span>
            </div>
          </div>
        </div>
        
        {/* Banque classique */}
        <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center">
              <Landmark className="w-5 h-5 text-gray-400" />
            </div>
            <div>
              <h4 className="text-lg font-bold text-white">Prêt bancaire</h4>
              <p className="text-gray-400 text-xs">Taux bancaire moyen (estimation) ~{TAUX_BANQUE}%</p>
            </div>
          </div>
          
          <div className="space-y-3 mb-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-400 text-sm">Taux d'intérêt</span>
              <span className="text-red-400 font-bold text-lg">{TAUX_BANQUE}%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400 text-sm">Réduction</span>
              <span className="text-gray-500 font-bold text-lg">0%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400 text-sm">Montant à rembourser</span>
              <span className="text-white font-bold">{formatMontant(montant)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400 text-sm">Intérêts sur 20 ans</span>
              <span className="text-red-400 font-bold">+{formatMontant(interetsBanque)}</span>
            </div>
            <div className="border-t border-gray-700 pt-3 flex justify-between items-center">
              <span className="text-gray-400 text-sm">Mensualité</span>
              <span className="text-white font-bold text-xl">{formatMontant(mensualiteBanque)}/mois</span>
            </div>
          </div>
          
          <div className="bg-gray-700/50 rounded-lg p-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-400 font-medium">Coût total</span>
              <span className="text-white font-bold text-2xl">{formatMontant(coutTotalBanque)}</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Économies */}
      {economie > 0 && (
        <div className="max-w-4xl mx-auto mt-6">
          <div className="bg-gradient-to-r from-yellow-500/20 to-green-500/20 border border-yellow-500/30 rounded-xl p-4 text-center">
            <p className="text-yellow-400 font-medium mb-1">💰 Avec le Rénopack, vous économisez</p>
            <p className="text-3xl font-bold text-white">{formatMontant(economie)}</p>
            <p className="text-gray-400 text-sm mt-1">sur la durée totale du prêt (audit inclus)</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Section "En bref" - Rénopack expliqué
const EnBrefSection = () => (
  <section id="renopack-explique" className="py-12 lg:py-16 bg-gray-900">
    <div className="container mx-auto px-4 lg:px-8">
      
      {/* Titre principal */}
      <div className="text-center mb-12">
        <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
          Vous voulez faire des <span className="text-green-400">travaux de rénovation</span> ?
        </h2>
        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
          La Région Wallonne vous propose un <strong className="text-yellow-400">prêt à taux 0%</strong> avec jusqu'à <strong className="text-yellow-400">50% de réduction</strong> sur le montant à rembourser.
        </p>
      </div>
      
      {/* Qu'est-ce que le Rénopack */}
      <div className="max-w-4xl mx-auto mb-12">
        <div className="bg-gray-800 rounded-2xl p-8 border border-gray-700">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center">
              <Lightbulb className="w-6 h-6 text-yellow-400" />
            </div>
            <h3 className="text-2xl font-bold text-white">Qu'est-ce que le Rénopack ?</h3>
          </div>
          <p className="text-gray-300 text-lg leading-relaxed">
            C'est la combinaison d'une <span className="text-green-400 font-semibold">réduction régionale</span> et d'un <span className="text-green-400 font-semibold">prêt à taux zéro (0%)</span>.<br/>
            Un <span className="text-yellow-400 font-semibold">financement tout-en-un</span> pour réaliser vos travaux de rénovation énergétique sans impacter lourdement votre budget.
          </p>
        </div>
      </div>
      
      {/* Travaux éligibles */}
      <div className="max-w-5xl mx-auto mb-12">
        <h3 className="text-xl lg:text-2xl font-bold text-white text-center mb-6">
          <Wrench className="w-6 h-6 inline-block mr-2 text-gray-400" />
          Quels travaux pouvez-vous financer ?
        </h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Home, title: "Isolation thermique", desc: "Toiture, murs, crépis, sol", color: "text-orange-400" },
            { icon: Sun, title: "Photovoltaïque", desc: "Autoproduction d'électricité", color: "text-yellow-400" },
            { icon: Flame, title: "Pompe à chaleur", desc: "Remplacement de chaudière", color: "text-red-400" },
            { icon: DoorOpen, title: "Châssis & vitrages", desc: "Fenêtres performantes", color: "text-blue-400" }
          ].map((item, i) => (
            <div key={i} className="bg-gray-800/50 rounded-xl p-5 border border-gray-700/50 hover:border-gray-600 transition-colors">
              <item.icon className={`w-8 h-8 ${item.color} mb-3`} />
              <h4 className={`font-bold ${item.color} mb-1`}>{item.title}</h4>
              <p className="text-gray-400 text-sm">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
      
      {/* Pourquoi en profiter */}
      <div className="max-w-4xl mx-auto mb-12">
        <h3 className="text-xl lg:text-2xl font-bold text-white text-center mb-6">
          <TrendingUp className="w-6 h-6 inline-block mr-2 text-green-400" />
          Pourquoi en profiter dès maintenant ?
        </h3>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { title: "Taux 0%", desc: "Empruntez sans payer d'intérêts", color: "text-green-400" },
            { title: "Factures réduites", desc: "Vos économies d'énergie compensent les mensualités", color: "text-green-400" },
            { title: "Valorisation", desc: "Améliorez le certificat PEB de votre maison", color: "text-green-400" }
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3 bg-gray-800/30 rounded-lg p-4">
              <CheckCircle2 className={`w-6 h-6 ${item.color} flex-shrink-0 mt-0.5`} />
              <div>
                <p className={`font-bold ${item.color}`}>{item.title}</p>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Comment l'obtenir - LA CLÉ */}
      <div className="max-w-4xl mx-auto mb-12">
        <div className="bg-gradient-to-r from-green-600/20 to-green-500/10 border-2 border-green-500/50 rounded-2xl p-8">
          <h3 className="text-xl lg:text-2xl font-bold text-white text-center mb-6">
            <Target className="w-6 h-6 inline-block mr-2 text-green-400" />
            Comment obtenir ce prêt à 0% ?
          </h3>
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8">
            <div className="flex items-center gap-3 bg-gray-800 rounded-xl px-6 py-4">
              <span className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-lg">1</span>
              <div>
                <p className="font-bold text-white">Faire un audit logement</p>
                <p className="text-green-400 text-sm font-medium">Obligatoire et finançable</p>
              </div>
            </div>
            <ArrowRight className="w-6 h-6 text-green-400 hidden md:block" />
            <ChevronRight className="w-6 h-6 text-green-400 rotate-90 md:hidden" />
            <div className="flex items-center gap-3 bg-gray-800 rounded-xl px-6 py-4">
              <span className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-lg">2</span>
              <div>
                <p className="font-bold text-white">Demander le Rénopack</p>
                <p className="text-gray-400 text-sm">Via SWCS ou Fonds du Logement</p>
              </div>
            </div>
            <ArrowRight className="w-6 h-6 text-green-400 hidden md:block" />
            <ChevronRight className="w-6 h-6 text-green-400 rotate-90 md:hidden" />
            <div className="flex items-center gap-3 bg-gray-800 rounded-xl px-6 py-4">
              <span className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-lg">3</span>
              <div>
                <p className="font-bold text-white">Réaliser vos travaux</p>
                <p className="text-gray-400 text-sm">Avec un prêt à 0% !</p>
              </div>
            </div>
          </div>
          <p className="text-center text-green-300 mt-6 font-medium">
            <AlertCircle className="w-5 h-5 inline-block mr-1" />
            L'audit logement est la <strong>clé d'entrée</strong> pour accéder au Rénopack
          </p>
        </div>
      </div>
      
      {/* Comparateur de prêts */}
      <LoanComparator />
      
      <div className="text-center mt-10">
        <p className="text-gray-400 mb-4">Obtenez votre devis d'audit et débloquez l'accès au prêt à 0%</p>
        <Button asChild size="lg" className="bg-green-500 hover:bg-green-600 text-white h-14 max-w-full px-5 sm:px-8 text-base sm:text-lg">
          <a href="#formulaire" onClick={() => trackCTAClick('EnBref', 'Devis')}>
            Demander mon devis d'audit gratuit
            <ArrowRight className="w-5 h-5 ml-2" />
          </a>
        </Button>
      </div>
    </div>
  </section>
);

// Hero Section V2 - Split screen with integrated form
const HeroSectionV2 = ({ onFormSubmit, isSubmitting, onOpenPrivacy }) => {
  const [form, setForm] = useState({ nom: '', prenom: '', email: '', telephone: '', code_postal: '', type_logement: '', consent_rgpd: false });
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.telephone) { toast.error("Veuillez renseigner votre email et téléphone"); return; }
    if (!form.consent_rgpd) { toast.error("Veuillez accepter la politique de confidentialité"); return; }
    trackFormSubmit('form');
    await onFormSubmit(form);
    setForm({ nom: '', prenom: '', email: '', telephone: '', code_postal: '', type_logement: '', consent_rgpd: false });
  };

  return (
    <section id="top" className="relative min-h-screen lg:min-h-[90vh] bg-gradient-to-br from-green-900 via-green-800 to-green-900">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}} />
      </div>
      
      <div className="container mx-auto px-4 lg:px-8 py-6 lg:py-20 relative">
        {/* Mobile: Content above form */}
        <div className="lg:hidden text-white text-center mb-6">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src="/images/logo-wallonie-small.png" alt="Région Wallonne" className="h-10 w-auto" />
            <span className="text-green-200 font-medium text-sm">Programme officiel Région Wallonne</span>
          </div>
          
          <h1 className="text-2xl font-bold mb-3 leading-tight">
            Bénéficiez du <span className="text-yellow-400">Rénopack</span><br/>
            prêt à taux 0% + jusqu'à 50% de réduction
          </h1>
          
          <p className="text-base text-green-100 leading-relaxed">
            L'audit logement est <strong className="text-white">obligatoire</strong> pour accéder au Rénopack (prêt à taux 0%). 
            Recevez votre devis gratuit d'un auditeur agréé proche de chez vous.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left: Content - Desktop only */}
          <div className="hidden lg:block text-white text-left">
            <div className="flex items-center justify-start gap-3 mb-6">
              <img src="/images/logo-wallonie-small.png" alt="Région Wallonne" className="h-12 w-auto" />
              <span className="text-green-200 font-medium text-base">Programme officiel Région Wallonne</span>
            </div>
            
            <h1 className="text-5xl font-bold mb-6 leading-tight">
              Rénovez avec le <span className="text-yellow-400">Rénopack</span><br/>
              Prêt à taux 0% + jusqu'à 50% de réduction
            </h1>
            
            <p className="text-xl text-green-100 mb-8 leading-relaxed">
              Votre logement est énergivore ? La Région Wallonne finance vos travaux.<br/>
              <strong className="text-white">L'audit logement est obligatoire</strong> — obtenez votre devis gratuit.
            </p>
            
            <div className="grid grid-cols-2 gap-4 mb-8 text-base">
              {[
                { icon: CheckCircle2, text: "Service 100% gratuit" },
                { icon: Clock, text: "Réponse sous 24h" },
                { icon: ShieldCheck, text: "Auditeurs agréés" },
                { icon: MapPin, text: "Toute la Wallonie" }
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-start gap-2 text-green-100">
                  <item.icon className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
            
            <div className="flex items-center gap-4">
              <a href={`tel:${PHONE_LINK}`} onClick={() => trackPhoneClick('Hero V2')} className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-6 py-3 rounded-full text-white font-medium transition-colors">
                <Phone className="w-5 h-5" />
                Contacter un auditeur
              </a>
            </div>
          </div>
          
          {/* Right: Form */}
          <div id="formulaire" className="bg-white/95 backdrop-blur-sm rounded-2xl p-4 lg:p-8 shadow-2xl scroll-mt-20">
            {/* Mobile only: Key badges above form */}
            <div className="flex lg:hidden justify-center gap-4 mb-3 pb-3 border-b border-gray-100 text-sm">
              <div className="flex items-center gap-1.5 text-green-700 font-medium">
                <ShieldCheck className="w-4 h-4 text-green-600" />
                <span>Auditeurs agréés</span>
              </div>
              <div className="flex items-center gap-1.5 text-green-700 font-medium">
                <MapPin className="w-4 h-4 text-green-600" />
                <span>Toute la Wallonie</span>
              </div>
            </div>
            
            <div className="text-center mb-4 lg:mb-6">
              <h2 className="text-lg lg:text-2xl font-bold text-gray-900">Recevez un devis gratuit d'un auditeur agréé sous 24h</h2>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-3 lg:space-y-4">
              <div className="grid grid-cols-2 gap-2 lg:gap-3">
                <div>
                  <Label className="text-xs lg:text-sm font-medium text-gray-700 mb-1 block">Prénom</Label>
                  <Input value={form.prenom} onChange={(e) => setForm({...form, prenom: e.target.value})} 
                    className="h-9 lg:h-11 text-sm" placeholder="Jean" data-testid="input-prenom" />
                </div>
                <div>
                  <Label className="text-xs lg:text-sm font-medium text-gray-700 mb-1 block">Nom</Label>
                  <Input value={form.nom} onChange={(e) => setForm({...form, nom: e.target.value})} 
                    className="h-9 lg:h-11 text-sm" placeholder="Dupont" data-testid="input-nom" />
                </div>
              </div>
              
              <div>
                <Label className="text-xs lg:text-sm font-medium text-gray-700 mb-1 block">Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required 
                  className="h-9 lg:h-11 text-sm" placeholder="jean.dupont@email.be" data-testid="input-email" />
              </div>
              
              <div>
                <Label className="text-xs lg:text-sm font-medium text-gray-700 mb-1 block">Téléphone *</Label>
                <Input type="tel" value={form.telephone} onChange={(e) => setForm({...form, telephone: e.target.value})} required 
                  className="h-9 lg:h-11 text-sm" placeholder="+32 4XX XX XX XX" data-testid="input-telephone" />
              </div>
              
              <div className="grid grid-cols-2 gap-2 lg:gap-3">
                <div>
                  <Label className="text-xs lg:text-sm font-medium text-gray-700 mb-1 block">Code postal</Label>
                  <Input value={form.code_postal} onChange={(e) => setForm({...form, code_postal: e.target.value})} 
                    className="h-9 lg:h-11 text-sm" placeholder="5000" data-testid="input-code-postal" />
                </div>
                <div>
                  <Label className="text-xs lg:text-sm font-medium text-gray-700 mb-1 block">Type de bien</Label>
                  <Select value={form.type_logement} onValueChange={(v) => setForm({...form, type_logement: v})}>
                    <SelectTrigger className="h-9 lg:h-11 text-sm" data-testid="select-type-logement"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="studio">Studio</SelectItem>
                      <SelectItem value="appartement">Appartement</SelectItem>
                      <SelectItem value="maison-2-3-sans-annexe">Maison 2-3 façades (sans annexe)</SelectItem>
                      <SelectItem value="maison-2-3-avec-annexe">Maison 2-3 façades (avec annexe)</SelectItem>
                      <SelectItem value="maison-4-facades-350">Maison 4 façades ≤ 350 m²</SelectItem>
                      <SelectItem value="maison-350-plus">Maison &gt; 350 m²</SelectItem>
                      <SelectItem value="immeuble">Immeuble</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-start gap-3 pt-2">
                <Checkbox 
                  id="rgpd-v2" 
                  checked={form.consent_rgpd} 
                  onCheckedChange={(checked) => setForm({...form, consent_rgpd: checked === true})} 
                  className="h-6 w-6 mt-0.5 border-2 border-gray-300 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600 cursor-pointer flex-shrink-0" 
                  data-testid="checkbox-rgpd" 
                />
                <label htmlFor="rgpd-v2" className="text-sm text-gray-600 leading-relaxed cursor-pointer select-none">
                  J'accepte la <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpenPrivacy(); }} className="text-green-600 underline hover:text-green-700">politique de confidentialité</button> *
                </label>
              </div>
              <AdsConsentCheckbox id="ads-consent-hero" checked={form.consent_google_ads} onChange={(v) => setForm({...form, consent_google_ads: v})} />
              
              <Button type="submit" disabled={isSubmitting} 
                className="w-full bg-green-600 hover:bg-green-700 h-14 text-lg font-semibold shadow-lg shadow-green-600/20" 
                data-testid="submit-button">
                {isSubmitting ? 'Envoi en cours...' : 'Recevoir mon devis gratuit'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              
              <p className="text-center text-gray-400 text-xs">
                Service gratuit • Sans engagement • Réponse sous 24h
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Version selector - based on URL path
const getHeroVersion = () => {
  if (typeof window !== 'undefined') {
    return window.location.pathname === '/wallonie' ? 'v2' : 'v1';
  }
  return 'v1';
};

const HeroSection = (props) => {
  const version = getHeroVersion();
  if (version === 'v2') {
    return <HeroSectionV2 {...props} />;
  }
  return <HeroSectionV1 />;
};

// Trust Banner with Feuille de Route - Just below Hero
const TrustBannerSection = () => (
  <section className="py-12 bg-gray-50 border-b border-gray-200">
    <div className="container mx-auto px-4 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-8 items-center max-w-6xl mx-auto">
        {/* Left: Feuille de Route */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
          <div className="bg-green-600 text-white text-center py-2 text-sm font-medium">
            Exemple de Feuille de Route Officielle
          </div>
          <picture>
            <source srcSet="/images/feuille-route.webp" type="image/webp" />
            <img 
              src="/images/feuille-route.png" 
              alt="Feuille de route audit logement Wallonie - Exemple officiel" 
              className="w-full h-auto"
              loading="lazy"
            />
          </picture>
        </div>
        
        {/* Right: Trust elements */}
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <img src="/images/logos/logo-wallonie-official.png" alt="Logo Région Wallonne" className="h-16 w-auto" />
            <div>
              <p className="text-lg font-bold text-gray-900">Programme officiel</p>
              <p className="text-gray-600">Région Wallonne</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-gray-900">Auditeurs agréés par la Région</p>
                <p className="text-gray-600 text-sm">Tous nos partenaires sont certifiés et reconnus officiellement</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-gray-900">Rénopack : jusqu'à 50% de réduction</p>
                <p className="text-gray-600 text-sm">Prêt à taux 0% pour les catégories C1 à C3 (C4 : Rénoprêt)</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-gray-900">Validité de 8 ans</p>
                <p className="text-gray-600 text-sm">Planifiez vos travaux sereinement après l'audit</p>
              </div>
            </div>
          </div>
          
          <Button asChild size="lg" className="w-full bg-green-600 hover:bg-green-700 h-14 text-lg">
            <a href="#formulaire" onClick={() => trackCTAClick('Trust Banner', 'Obtenir mon devis')}>
              Obtenir mon devis gratuit
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
        </div>
      </div>
    </div>
  </section>
);

// Prime Audit Section
const PrimeAuditSection = () => (
  <section className="py-16 lg:py-20 bg-white">
    <div className="container mx-auto px-4 lg:px-8">
      <div className="text-center mb-12">
        <span className="inline-block bg-orange-100 text-orange-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
          NOUVEAU RÉGIME - 1er octobre 2026
        </span>
        <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
          Rénopack : Prêt à taux 0% + réduction
        </h2>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          <strong>Nouveauté 2026 :</strong> Le système de primes est remplacé par le <strong>Rénopack</strong>, un prêt à taux 0% 
          avec une <strong>réduction du montant à rembourser</strong> pouvant aller jusqu'à 50%.
        </p>
      </div>
      
      <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto mb-12">
        {[
          { cat: "C1", title: "≤ 28.900€", taux: "0%", reduction: "50%", desc: "Revenus précaires", color: "bg-green-600" },
          { cat: "C2", title: "28.900€ - 41.100€", taux: "0%", reduction: "40%", desc: "Revenus modestes", color: "bg-green-500" },
          { cat: "C3", title: "41.100€ - 67.100€", taux: "0%", reduction: "15%", desc: "Revenus moyens", color: "bg-teal-500" },
          { cat: "C4", title: "67.100€ - 122.800€", taux: "Variable", reduction: "0%", desc: "Revenus supérieurs (Rénoprêt)", color: "bg-teal-400" }
        ].map((item, i) => (
          <div key={i} className={`${item.color} text-white rounded-2xl p-6 text-center shadow-lg`}>
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="font-bold text-lg">{item.cat}</span>
            </div>
            <p className="text-white/80 text-sm mb-1">{item.desc}</p>
            <p className="text-3xl font-bold mb-1">-{item.reduction}</p>
            <p className="text-white/90 text-sm">Taux : {item.taux}</p>
            <p className="text-white/70 text-xs mt-1">{item.title}</p>
          </div>
        ))}
      </div>
      <p className="text-center text-xs text-gray-500 max-w-3xl mx-auto -mt-8 mb-12" data-testid="renopack-ecomateriaux-note">
        Réductions maximales, dont 5% de bonus pour l'utilisation d'écomatériaux. Revenus imposables indexés au 1er janvier 2026, 5.000€ déduits par personne à charge.
      </p>
      
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-2xl p-6 max-w-4xl mx-auto mb-8">
        <div className="flex items-start gap-4">
          <AlertCircle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-orange-800 font-bold mb-2">Conditions du nouveau régime (PEB obligatoire)</p>
            <ul className="text-orange-700 text-sm space-y-1">
              <li>• <strong>Label G ou F</strong> : travaux doivent atteindre minimum le <strong>label D</strong></li>
              <li>• <strong>Label E</strong> : travaux doivent atteindre minimum le <strong>label C</strong></li>
              <li>• <strong>Audit requis</strong> : réalisé ou actualisé max. 1 an avant la demande (finançable dans le prêt). Exception : pour C1 ou avec Accesspack, le prêteur peut estimer le label PEB</li>
              <li>• <strong>Montant max</strong> : jusqu'à <strong>75.000€</strong> pour une maison (vs 60.000€ avant)</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <Info className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-800 font-medium mb-1">Calcul du revenu de référence</p>
              <p className="text-amber-700 text-sm">
                Revenu globalement imposable (RGI), diminué de <strong>5.000€ par personne à charge</strong>. Montants indexés au 1er janvier 2026.
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <Calendar className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-blue-800 font-medium mb-1">Validité de l'audit</p>
              <p className="text-blue-700 text-sm">
                L'audit doit avoir été réalisé <strong>max. 1 an</strong> avant l'introduction de la demande de prêt.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// What is Audit Section
const WhatIsAuditSection = () => (
  <section className="py-16 lg:py-20 bg-gray-50">
    <div className="container mx-auto px-4 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="inline-block bg-green-100 text-green-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
            Comprendre l'audit
          </span>
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            Qu'est-ce qu'un audit logement ?
          </h2>
          
          <p className="text-gray-600 mb-6 leading-relaxed">
            L'<strong>audit logement</strong> (aussi appelé audit énergétique PAE2) est une <strong>analyse approfondie</strong> de la 
            performance énergétique de votre habitation, réalisée par un <strong>auditeur agréé par la Région Wallonne</strong>.
          </p>
          
          <p className="text-gray-600 mb-6 leading-relaxed">
            Lors de la visite (1h30 à 2h00 selon la taille du bien), l'auditeur examine minutieusement votre logement et identifie 
            les points faibles ainsi que les améliorations possibles.
          </p>
          
          <h3 className="font-bold text-gray-900 mb-4">L'auditeur analyse :</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            {[
              "L'enveloppe du bâtiment (murs, toiture, planchers)",
              "Les menuiseries (fenêtres, portes)",
              "Les systèmes de chauffage",
              "La production d'eau chaude sanitaire",
              "La ventilation et qualité de l'air",
              "L'installation électrique",
              "Les éventuels problèmes d'humidité",
              "La consommation énergétique globale"
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 text-sm">{item}</span>
              </div>
            ))}
          </div>
          
          <div className="bg-green-50 border border-green-200 rounded-xl p-5">
            <h4 className="font-bold text-green-800 mb-2">Vous recevez ensuite :</h4>
            <p className="text-green-700 text-sm">
              Un <strong>rapport détaillé</strong> avec des recommandations de travaux prioritaires, une estimation des coûts, 
              les économies potentielles et surtout : <strong>l'accès au Rénopack et au Rénoprêt</strong> de la Région Wallonne.
            </p>
          </div>
        </div>
        
        <div className="relative">
          <div className="grid grid-cols-2 gap-4">
            <OptimizedImage index={1} alt="Audit isolation toiture" className="rounded-xl shadow-lg w-full h-48 object-cover" />
            <OptimizedImage index={2} alt="Audit chauffage" className="rounded-xl shadow-lg w-full h-48 object-cover mt-8" />
            <OptimizedImage index={3} alt="Audit menuiseries" className="rounded-xl shadow-lg w-full h-48 object-cover" />
            <OptimizedImage index={4} alt="Audit énergétique" className="rounded-xl shadow-lg w-full h-48 object-cover mt-8" />
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Why Audit Section
const WhyAuditSection = () => (
  <section className="py-16 lg:py-20 bg-white">
    <div className="container mx-auto px-4 lg:px-8">
      <div className="text-center mb-12">
        <span className="inline-block bg-amber-100 text-amber-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
          Obligatoire depuis 2019
        </span>
        <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
          Pourquoi réaliser un audit logement ?
        </h2>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          Depuis le <strong>1er octobre 2026</strong>, l'audit logement est <strong>requis</strong> pour accéder au Rénopack 
          et au Rénoprêt de la Région Wallonne.
        </p>
      </div>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
        {[
          { icon: PiggyBank, title: "Audit intégré au Rénopack", desc: "L'audit est désormais finançable dans votre prêt Rénopack à taux 0%. Plus besoin d'avancer les frais !", color: "bg-green-100 text-green-600" },
          { icon: CircleDollarSign, title: "Accès aux aides rénovation", desc: "Sans audit, pas de Rénopack ni de Rénoprêt ! L'audit est le sésame obligatoire pour toutes les aides.", color: "bg-blue-100 text-blue-600" },
          { icon: Banknote, title: "Rénopack – prêt à taux 0%", desc: "Prêt à 0% avec réduction jusqu'à 50% du montant à rembourser. Jusqu'à 75.000€ pour une maison (nouveau plafond 2026).", color: "bg-purple-100 text-purple-600" },
          { icon: TrendingUp, title: "Valorisation du bien", desc: "Améliorez votre label PEB et augmentez la valeur de votre bien de 10 à 20%.", color: "bg-amber-100 text-amber-600" }
        ].map((item, i) => (
          <div key={i} className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className={`w-14 h-14 ${item.color} rounded-xl flex items-center justify-center mb-4`}>
              <item.icon className="w-7 h-7" />
            </div>
            <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
            <p className="text-gray-600 text-sm">{item.desc}</p>
          </div>
        ))}
      </div>
      
      <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-bold text-red-800 mb-2">Audit AVANT les travaux !</h4>
              <p className="text-red-700 text-sm">
                Pour être éligible aux aides, l'audit logement doit <strong>impérativement être réalisé AVANT le début des travaux</strong>. 
                Si vous commencez les travaux sans audit, vous perdez le bénéfice des aides Rénopack/Rénoprêt.
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <Calendar className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-bold text-blue-800 mb-2">Changement au 1er octobre 2026</h4>
              <p className="text-blue-700 text-sm">
                <strong>Jusqu'au 30/09/2026</strong> : primes Habitation (audit non obligatoire pour la toiture).<br/>
                <strong>Depuis le 01/10/2026</strong> : les primes sont remplacées par le Rénopack et le Rénoprêt, qui demandent un audit (sauf exception C1 / Accesspack).
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Pricing Section
const PricingSection = () => (
  <section className="py-16 lg:py-20 bg-gray-900 text-white">
    <div className="container mx-auto px-4 lg:px-8">
      <div className="text-center mb-12">
        <span className="inline-block bg-green-500/20 text-green-400 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
          Tarifs transparents
        </span>
        <h2 className="text-3xl lg:text-4xl font-bold mb-4">
          Combien coûte un audit logement ?
        </h2>
        <p className="text-gray-400 max-w-2xl mx-auto">
          Le prix varie selon la taille du logement. <strong className="text-green-400">Bonne nouvelle :</strong> l'audit est désormais finançable dans votre Rénopack !
        </p>
      </div>
      
      <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-12">
        {[
          { type: "Studio / Appartement", price: "595€ - 795€", surface: "Toute surface", info: "Finançable via Rénopack", final: "Intégré au prêt" },
          { type: "Maison", price: "995€ - 1.265€", surface: "2, 3 ou 4 façades", info: "Finançable via Rénopack", final: "Intégré au prêt", popular: true },
          { type: "Immeuble", price: "Sur devis", surface: "Plusieurs logements", info: "Rénoprêt copropriété", final: "Jusqu'à 60.000€/lot" }
        ].map((item, i) => (
          <div key={i} className={`rounded-2xl p-8 ${item.popular ? 'bg-green-600 ring-4 ring-green-400' : 'bg-gray-800'}`}>
            {item.popular && <p className="text-green-200 text-sm font-medium mb-2">Le plus demandé</p>}
            <h3 className="text-xl font-bold mb-1">{item.type}</h3>
            <p className="text-gray-400 text-sm mb-4">{item.surface}</p>
            <p className="text-4xl font-bold mb-4">{item.price}</p>
            <div className="border-t border-white/20 pt-4">
              <p className="text-sm text-gray-400">{item.info}</p>
              <p className="font-bold text-green-400 mt-1">{item.final}</p>
            </div>
          </div>
        ))}
      </div>
      
      <p className="text-center text-gray-500 text-sm">
        * L'audit fait partie des investissements finançables via le Rénopack (nouveau régime depuis le 1er octobre 2026)
      </p>
    </div>
  </section>
);

// Works Section
const WorksSection = () => (
  <section className="py-16 lg:py-20 bg-white">
    <div className="container mx-auto px-4 lg:px-8">
      <div className="text-center mb-12">
        <span className="inline-block bg-blue-100 text-blue-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
          Travaux éligibles
        </span>
        <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
          Quels travaux financer avec le Rénopack ?
        </h2>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          Suite à l'audit, le Rénopack finance l'ensemble des travaux de votre projet de rénovation, calculé globalement et non plus poste par poste.
        </p>
      </div>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[
          { icon: Building, title: "Toiture / Combles", items: ["Isolation thermique standard", "Isolation biosourcée", "Remplacement couverture", "Charpente et eaux pluviales"] },
          { icon: Building2, title: "Murs", items: ["Isolation intérieure/extérieure", "Isolation biosourcée", "Assèchement infiltrations", "Renforcement murs"] },
          { icon: Home, title: "Sols", items: ["Isolation thermique standard", "Isolation biosourcée", "Supports porteurs", "Finitions planchers"] },
          { icon: DoorOpen, title: "Menuiseries", items: ["Châssis + vitrage", "Double/triple vitrage", "Portes extérieures"] },
          { icon: Flame, title: "Chauffage", items: ["Pompe à chaleur", "Chaudière biomasse", "Poêle biomasse"] },
          { icon: Wind, title: "Ventilation", items: ["VMC simple flux", "VMC double flux", "Systèmes partiels"] },
          { icon: Droplets, title: "Eau chaude", items: ["PAC eau chaude", "Chauffe-eau solaire"] },
          { icon: Zap, title: "Conformité", items: ["Installation électrique", "Installation gaz"] },
          { icon: Shield, title: "Salubrité", items: ["Élimination radon", "Traitement mérule", "Humidité ascensionnelle"] }
        ].map((item, i) => (
          <div key={i} className="bg-gray-50 rounded-2xl p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <item.icon className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">{item.title}</h3>
              </div>
            </div>
            <ul className="space-y-2">
              {item.items.map((work, j) => (
                <li key={j} className="flex items-center gap-2 text-gray-600 text-sm">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  {work}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      
      <p className="text-center text-gray-500 text-sm mt-8">
        * Jusqu'à 75.000€ pour une maison. La réduction dépend de votre catégorie de revenus (C1 à C3, C4 : Rénoprêt) et le projet doit atteindre le label PEB requis.
      </p>
      
      <div className="text-center mt-8">
        <Button asChild size="lg" className="bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-600/20">
          <a href="/simulateur" className="gap-2">
            Simuler mon Rénopack <ArrowRight className="w-5 h-5" />
          </a>
        </Button>
      </div>
    </div>
  </section>
);

// Process Section
const ProcessSection = () => (
  <section className="py-16 lg:py-20 bg-green-50">
    <div className="container mx-auto px-4 lg:px-8">
      <div className="text-center mb-12">
        <span className="inline-block bg-green-600 text-white text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
          Processus simple
        </span>
        <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
          Comment ça marche ?
        </h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Notre service de mise en relation est 100% gratuit et sans engagement
        </p>
      </div>
      
      <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
        {[
          { icon: ClipboardCheck, step: "1", title: "Demande de devis", desc: "Remplissez notre formulaire en quelques clics. C'est gratuit, rapide et sans engagement. Nous ne partageons jamais vos données sans votre accord." },
          { icon: Users, step: "2", title: "Mise en relation", desc: "Nous transmettons votre demande à un auditeur agréé de votre région. Vous recevez une offre personnalisée sous 24h." },
          { icon: FileSearch, step: "3", title: "Visite de l'auditeur", desc: "L'auditeur se rend à votre logement (1h30 à 2h) pour analyser votre habitation et établir un état des lieux énergétique complet." },
          { icon: FileCheck, step: "4", title: "Rapport & Rénopack", desc: "Vous recevez votre rapport détaillé avec les recommandations. Vous pouvez maintenant demander le Rénopack (prêt à taux 0%) ou le Rénoprêt !" }
        ].map((item, i) => (
          <div key={i} className="text-center">
            <div className="relative inline-block mb-6">
              <div className="w-20 h-20 bg-green-600 rounded-2xl flex items-center justify-center shadow-lg shadow-green-600/30">
                <item.icon className="w-10 h-10 text-white" />
              </div>
              <span className="absolute -top-2 -right-2 w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold shadow-lg">
                {item.step}
              </span>
            </div>
            <h3 className="font-bold text-gray-900 text-lg mb-3">{item.title}</h3>
            <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Form Section
const FormSection = ({ onFormSubmit, isSubmitting, onOpenPrivacy }) => {
  const [form, setForm] = useState({ civilite: '', nom: '', prenom: '', email: '', telephone: '', code_postal: '', type_logement: '', consent_rgpd: false });
  
  const AUDIT_TARIFS = [
    { value: "studio", label: "Studio", prix: "595€" },
    { value: "appartement", label: "Appartement", prix: "795€" },
    { value: "duplex", label: "Duplex", prix: "895€" },
    { value: "maison-2-3-facades", label: "Maison 2 ou 3 façades", prix: "995€" },
    { value: "maison-4-facades-300", label: "Maison 4 façades ≤ 300 m²", prix: "1.065€" },
    { value: "maison-4-facades-400", label: "Maison 4 façades ≤ 400 m²", prix: "1.165€" },
    { value: "maison-4-facades-500", label: "Maison 4 façades ≤ 500 m²", prix: "1.265€" },
    { value: "immeuble", label: "Immeuble", prix: "Sur devis" },
  ];
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.type_logement) { toast.error("Veuillez sélectionner votre type de bien"); return; }
    if (!form.email || !form.telephone) { toast.error("Veuillez renseigner votre email et téléphone"); return; }
    if (!form.consent_rgpd) { toast.error("Veuillez accepter la politique de confidentialité"); return; }
    trackFormSubmit('form');
    await onFormSubmit(form);
    setForm({ civilite: '', nom: '', prenom: '', email: '', telephone: '', code_postal: '', type_logement: '', consent_rgpd: false });
  };

  return (
    <section id="formulaire" className="py-10 lg:py-12 bg-gray-50 scroll-mt-20">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-xl mx-auto">
          <div className="text-center mb-5">
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">Demander un devis pour un audit</h2>
            <p className="text-gray-600 text-sm">Un auditeur agréé vous rappelle dans la journée</p>
          </div>
          
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-5 lg:p-6 shadow-xl border border-gray-100">
            {/* Type de bien avec prix - EN PREMIER */}
            <div className="mb-4">
              <Label className="text-sm font-medium text-gray-700 mb-2 block">Type de bien *</Label>
              <div className="grid grid-cols-1 gap-1.5">
                {AUDIT_TARIFS.map((item) => (
                  <label
                    key={item.value}
                    className={`flex items-center justify-between p-2.5 border rounded-lg cursor-pointer transition-all ${
                      form.type_logement === item.value 
                        ? 'border-green-500 bg-green-50 ring-2 ring-green-500/20' 
                        : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <input
                        type="radio"
                        name="type_logement"
                        value={item.value}
                        checked={form.type_logement === item.value}
                        onChange={(e) => setForm({...form, type_logement: e.target.value})}
                        className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                      />
                      <span className="font-medium text-gray-900 text-sm">{item.label}</span>
                    </div>
                    <span className="font-semibold text-green-600 text-sm">{item.prix}</span>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Prime info */}
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-xs text-yellow-800">
                <strong>Bonne nouvelle :</strong> l'audit est finançable via le <strong>Rénopack</strong> (prêt à taux 0% avec jusqu'à 50% de réduction).
              </p>
            </div>
            
            {/* Civilité */}
            <div className="mb-3">
              <Label className="text-sm font-medium text-gray-700 mb-1.5 block">Civilité <span className="text-gray-400 font-normal">(optionnel)</span></Label>
              <div className="flex gap-2">
                {[{value:'M', label:'Monsieur'}, {value:'Mme', label:'Madame'}].map(opt => (
                  <label 
                    key={opt.value}
                    className={`cursor-pointer px-4 py-1.5 rounded-full border text-sm transition-all ${
                      form.civilite === opt.value 
                        ? 'border-green-500 bg-green-50 text-green-700 font-medium' 
                        : 'border-gray-200 text-gray-600 hover:border-green-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="civilite"
                      value={opt.value}
                      checked={form.civilite === opt.value}
                      onChange={() => setForm({...form, civilite: form.civilite === opt.value ? '' : opt.value})}
                      className="sr-only"
                      data-testid={`civilite-${opt.value.toLowerCase()}`}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-1 block">Prénom</Label>
                <Input value={form.prenom} onChange={(e) => setForm({...form, prenom: e.target.value})} 
                  className="h-10" placeholder="Jean" data-testid="input-prenom" />
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-1 block">Nom</Label>
                <Input value={form.nom} onChange={(e) => setForm({...form, nom: e.target.value})} 
                  className="h-10" placeholder="Dupont" data-testid="input-nom" />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-1 block">Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required 
                  className="h-10" placeholder="email@exemple.be" data-testid="input-email" />
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-1 block">Téléphone *</Label>
                <Input type="tel" value={form.telephone} onChange={(e) => setForm({...form, telephone: e.target.value})} required 
                  className="h-10" placeholder="+32 4XX XX XX XX" data-testid="input-telephone" />
              </div>
            </div>
            
            <div className="mb-4">
              <Label className="text-sm font-medium text-gray-700 mb-1 block">Code postal</Label>
              <Input value={form.code_postal} onChange={(e) => setForm({...form, code_postal: e.target.value})} 
                className="h-10 max-w-[120px]" placeholder="5000" data-testid="input-code-postal" />
            </div>
            
            <div className="flex items-start gap-3 mb-4">
              <Checkbox 
                id="rgpd" 
                checked={form.consent_rgpd} 
                onCheckedChange={(checked) => setForm({...form, consent_rgpd: checked === true})} 
                className="h-5 w-5 mt-0.5 border-2 border-gray-300 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600 cursor-pointer flex-shrink-0" 
                data-testid="checkbox-rgpd" 
              />
              <label htmlFor="rgpd" className="text-xs text-gray-600 leading-relaxed cursor-pointer select-none">
                J'accepte d'être recontacté(e) et la <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpenPrivacy(); }} className="text-green-600 underline hover:text-green-700" data-testid="form-privacy-link">politique de confidentialité</button> *
              </label>
            </div>
            <div className="mb-4">
              <AdsConsentCheckbox id="ads-consent-form" checked={form.consent_google_ads} onChange={(v) => setForm({...form, consent_google_ads: v})} />
            </div>
            
            <div className="space-y-2">
              <Button type="submit" disabled={isSubmitting} 
                className="w-full bg-green-600 hover:bg-green-700 h-12 text-base font-semibold" 
                data-testid="submit-button">
                {isSubmitting ? 'Envoi...' : 'Envoyer ma demande à un auditeur'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <p className="text-center text-xs text-gray-500">Un auditeur vous rappelle dans la journée</p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

// Provinces Section
const ProvincesSection = () => (
  <section className="py-12 bg-green-600">
    <div className="container mx-auto px-4 lg:px-8">
      <div className="text-center text-white">
        <h2 className="text-2xl font-bold mb-6">Auditeurs agréés dans toute la Wallonie</h2>
        <div className="flex flex-wrap justify-center gap-4">
          {[
            { name: "Namur", cities: "Namur, Dinant, Gembloux, Andenne..." },
            { name: "Liège", cities: "Liège, Verviers, Huy, Waremme..." },
            { name: "Hainaut", cities: "Charleroi, Mons, Tournai, La Louvière..." },
            { name: "Brabant Wallon", cities: "Wavre, Nivelles, Ottignies-LLN..." },
            { name: "Luxembourg", cities: "Arlon, Marche, Bastogne, Virton..." }
          ].map((p, i) => (
            <div key={i} className="bg-white/10 backdrop-blur rounded-xl px-5 py-3 text-left">
              <p className="font-bold flex items-center gap-2"><MapPin className="w-4 h-4" />{p.name}</p>
              <p className="text-green-200 text-xs mt-1">{p.cities}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// FAQ Section
const FAQSection = () => (
  <section className="py-16 lg:py-20 bg-gray-50" id="faq">
    <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
      <div className="text-center mb-12">
        <span className="inline-block bg-gray-200 text-gray-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
          FAQ
        </span>
        <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
          Questions fréquentes
        </h2>
      </div>
      
      <Accordion type="single" collapsible className="space-y-4">
        {[
          { q: "L'audit logement est-il obligatoire ?", a: "Depuis le 1er octobre 2026, l'audit est requis pour obtenir le Rénopack ou le Rénoprêt : le rapport doit avoir été réalisé ou actualisé au maximum 1 an avant la demande. Exception : pour la catégorie C1, ou en cas de demande Accesspack simultanée, l'organisme prêteur peut estimer le label PEB à la place d'un audit récent." },
          { q: "Quand faut-il réaliser l'audit logement ?", a: "L'audit énergétique doit être réalisé AVANT le début de vos travaux et au maximum 1 an avant votre demande de Rénopack (rapport réalisé ou actualisé). Bien planifié, il permet d'optimiser les aides et de réduire le coût de vos travaux." },
          { q: "Combien coûte un audit logement en Wallonie ?", a: "Le prix varie de 595€ pour un studio à 1.265€ pour une grande maison 4 façades. Bonne nouvelle : depuis le 1er octobre 2026, l'audit est finançable directement dans votre prêt Rénopack !" },
          { q: "Quelle est la durée de la visite de l'auditeur ?", a: "La durée varie de 1h30 à 2h00 selon la taille et la complexité de votre logement. L'auditeur examine l'ensemble du bâtiment, prend des mesures et discute avec vous de vos projets. Vous recevez ensuite le rapport complet sous quelques jours." },
          { q: "Qu'est-ce que le Rénopack (nouveau régime 2026) ?", a: "Le Rénopack est un prêt à taux 0% avec une réduction du montant à rembourser pouvant aller jusqu'à 50% (catégorie C1, dont 5% de bonus pour l'utilisation d'écomatériaux). Nouveau plafond : 75.000€ pour les maisons. Condition : votre logement doit avoir un label PEB F/G (objectif D) ou E (objectif C)." },
          { q: "Quelles sont les nouvelles conditions PEB ?", a: "Depuis le 1er octobre 2026, le Rénopack est réservé aux logements 'passoires énergétiques' : Label G ou F → travaux doivent atteindre minimum le label D. Label E → travaux doivent atteindre minimum le label C." },
          { q: "Qui peut bénéficier du Rénopack ?", a: "Le Rénopack concerne les catégories C1, C2 et C3 (revenus jusqu'à 67.100€). Pour la catégorie C4 (revenus supérieurs), les propriétaires-bailleurs et copropriétés, c'est le Rénoprêt (taux préférentiel) qui s'applique." },
          { q: "Êtes-vous un bureau d'audit ?", a: "Non, nous travaillons en collaboration avec un réseau d'auditeurs agréés en Région wallonne. Nous vous orientons vers l'auditeur le plus adapté à votre situation et à votre localisation, sans impact sur le prix de votre audit." }
        ].map((faq, i) => (
          <AccordionItem key={i} value={`faq-${i}`} className="bg-white rounded-xl border border-gray-200 px-6 shadow-sm" data-testid={`faq-${i+1}`}>
            <AccordionTrigger className="py-5 text-left font-semibold text-gray-900 hover:no-underline">
              <span className="flex items-center gap-3">
                <HelpCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                {faq.q}
              </span>
            </AccordionTrigger>
            <AccordionContent className="text-gray-600 pb-5 pl-8 leading-relaxed">{faq.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

// CTA Section
const CTASection = () => (
  <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
    <div className="container mx-auto px-4 lg:px-8 text-center">
      <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
        Prêt à bénéficier du Rénopack ?
      </h2>
      <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
        Notre réseau d'auditeurs agréés vous accompagne dans toute la Wallonie. 
        Demandez votre devis gratuit et lancez vos travaux de rénovation énergétique.
      </p>
      
      <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
        <Button asChild size="lg" className="bg-green-500 hover:bg-green-600 text-white h-14 px-10 text-lg shadow-xl shadow-green-500/20">
          <a href="#formulaire" onClick={() => trackCTAClick('Commander Audit CTA', 'Commander mon audit')}>Commander mon audit<ArrowRight className="w-5 h-5 ml-2" /></a>
        </Button>
        <Button asChild size="lg" variant="outline" className="border-2 border-gray-600 text-white hover:bg-white/10 h-14 px-10 text-lg">
          <a href={`tel:${PHONE_LINK}`} onClick={() => trackPhoneClick('CTA Section')}><Phone className="w-5 h-5 mr-2" />{PHONE_DISPLAY}</a>
        </Button>
      </div>
      
      <div className="flex flex-wrap justify-center gap-8">
        {[
          { value: "8 ans", label: "Validité de l'audit" },
          { value: "24h", label: "Délai de réponse" },
          { value: "100%", label: "Service gratuit" },
          { value: "50%", label: "Réduction max (C1)" }
        ].map((stat, i) => (
          <div key={i} className="text-center">
            <p className="text-3xl font-bold text-green-400">{stat.value}</p>
            <p className="text-gray-500 text-sm">{stat.label}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Footer
const Footer = ({ onOpenPrivacy, onOpenTerms }) => (
  <footer className="bg-white border-t border-gray-200 py-12">
    <div className="container mx-auto px-4 lg:px-8">
      <div className="grid md:grid-cols-4 gap-8 mb-8">
        <div className="md:col-span-2">
          <Logo />
          <p className="text-gray-500 mt-4 text-sm max-w-md leading-relaxed">
            Service gratuit de mise en relation avec des auditeurs agréés par la Région Wallonne. 
            Bénéficiez du Rénopack (prêt à taux 0%) pour votre audit logement et vos travaux de rénovation énergétique.
          </p>
          <a href={`tel:${PHONE_LINK}`} onClick={() => trackPhoneClick('Footer')} className="inline-flex items-center gap-2 text-green-600 font-semibold mt-4 hover:text-green-700">
            <Phone className="w-4 h-4" />{PHONE_DISPLAY}
          </a>
        </div>
        
        <div>
          <h4 className="font-semibold text-gray-900 mb-4">Nos services</h4>
          <ul className="space-y-2 text-sm text-gray-600">
            <li><a href="/" className="hover:text-green-600">Audit Logement</a></li>
            <li><a href="/certificat-peb" className="hover:text-green-600">Certificat PEB</a></li>
            <li><a href="/simulateur" className="hover:text-green-600">Simulateur Rénopack</a></li>
            <li><a href="#formulaire" className="hover:text-green-600">Demander un devis</a></li>
          </ul>
        </div>
        
        <div>
          <h4 className="font-semibold text-gray-900 mb-4">Légal</h4>
          <ul className="space-y-2 text-sm text-gray-600">
            <li><button onClick={onOpenPrivacy} className="hover:text-green-600" data-testid="footer-privacy-link">Politique de confidentialité</button></li>
            <li><button onClick={onOpenTerms} className="hover:text-green-600" data-testid="footer-terms-link">Conditions générales</button></li>
          </ul>
        </div>
      </div>
      
      <div className="border-t border-gray-100 pt-8 text-center text-gray-400 text-xs">
        © {new Date().getFullYear()} Audits Logement Wallonie · Édité par Ulysse Julien Berneau · NIE Y5618264E · Service de mise en relation gratuit
      </div>
    </div>
  </footer>
);

// Mobile CTA
const MobileCTA = ({ showCookies }) => (
  <div className="lg:hidden fixed bottom-4 left-4 right-4 z-40" style={{ marginBottom: showCookies ? '84px' : '0' }}>
    <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-3">
      <a href={`tel:${PHONE_LINK}`} onClick={() => trackPhoneClick('Mobile CTA')} 
        className="flex items-center justify-center gap-2 w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl font-semibold text-sm">
        <Phone className="w-4 h-4" />
        {PHONE_DISPLAY}
      </a>
    </div>
  </div>
);

// Cookie & Modals
const CookieBanner = ({ onAccept, onReject }) => (
  <div className="fixed bottom-3 lg:bottom-4 left-3 right-3 lg:left-auto lg:right-4 lg:max-w-md z-50 bg-white/95 backdrop-blur-sm border border-gray-200 shadow-xl rounded-xl p-3" data-testid="cookie-banner">
    <div className="flex items-center justify-between gap-3">
      <p className="text-xs text-gray-600"><Cookie className="w-3 h-3 inline mr-1 text-amber-500" />Nous utilisons des cookies de mesure Google Ads, seulement si vous acceptez.</p>
      <div className="flex gap-1.5 flex-shrink-0">
        <Button variant="outline" onClick={onReject} size="sm" className="h-7 px-3 text-xs rounded-full" data-testid="cookie-reject">Refuser</Button>
        <Button onClick={onAccept} size="sm" className="h-7 px-3 text-xs bg-green-600 hover:bg-green-700 rounded-full" data-testid="cookie-accept">Accepter</Button>
      </div>
    </div>
  </div>
);

const TermsContent = () => (
  <div className="text-gray-800 space-y-5 text-base leading-relaxed">
    <h3 className="text-xl font-bold text-gray-900">Conditions Générales et Mentions Légales</h3>
    
    <div className="bg-gray-50 p-4 rounded-lg">
      <p className="font-semibold text-gray-900 mb-2">Éditeur du site</p>
      <p>Ulysse Julien Berneau, Autónomo (Espagne)</p>
      <p>NIE : Y5618264E</p>
      <p>Adresse : Calle Castillejos 276, SA2A, 08025 Barcelone</p>
      <p>Email : <a href="mailto:info@agencia-ppc.es" className="text-green-600 underline">info@agencia-ppc.es</a></p>
      <p>Téléphone : +34 613 076 099</p>
    </div>
    
    <div>
      <p className="font-semibold text-gray-900 mb-2">Nature du service</p>
      <p>Ce site propose un service de mise en relation 100% gratuit entre particuliers et auditeurs agréés par la Région Wallonne. Ce site ne réalise pas directement les audits logement.</p>
    </div>
    
    <div>
      <p className="font-semibold text-gray-900 mb-2">Commission</p>
      <p>En tant qu'apporteur d'affaires, l'éditeur peut recevoir une commission de la part des auditeurs partenaires. Cette commission n'affecte en aucun cas le prix final proposé au client.</p>
    </div>
    
    <div>
      <p className="font-semibold text-gray-900 mb-2">Limitation de responsabilité</p>
      <p>Les tarifs et conditions des aides (Rénopack, Rénoprêt) indiqués sur ce site sont fournis à titre indicatif et peuvent varier selon la réglementation en vigueur de la Région Wallonne et votre situation personnelle. Nouveau régime applicable depuis le 1er octobre 2026.</p>
    </div>
    
    <div>
      <p className="font-semibold text-gray-900 mb-2">Droit applicable</p>
      <p>Le droit belge est applicable pour les utilisateurs résidant en Belgique. Tout litige sera soumis aux juridictions compétentes.</p>
    </div>
  </div>
);

// Main App
const LandingPage = () => {
  usePageTracking(); // Track pageview on route change
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCookies, setShowCookies] = useState(true);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('cookieConsent')) setShowCookies(false);
  }, []);

  const handleSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const utmParams = getUtmParams();
      const form_type = getFormType(data.source || 'form');
      const current_page = window.location.pathname;
      const res = await axios.post(`${API}/contact`, { 
        ...data, 
        ...utmParams, 
        form_type,
        current_page,
        landing_url: utmParams.landing_url || window.location.href
      });
      if (res.data.success) toast.success("Merci, bien reçu !", { description: `Nous vous recontacterons sous 24h. En cas d'urgence : ${PHONE_DISPLAY}`, duration: 6000 });
    } catch { toast.error("Erreur", { description: `Veuillez réessayer ou appeler le ${PHONE_DISPLAY}` }); }
    finally { setIsSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <SEOHead />
      <RegulationBanner />
      <Header />
      <main>
        <HeroSection onFormSubmit={handleSubmit} isSubmitting={isSubmitting} onOpenPrivacy={() => setPrivacyOpen(true)} />
        <EnBrefSection />
        <TrustBannerSection />
        <FormSection onFormSubmit={handleSubmit} isSubmitting={isSubmitting} onOpenPrivacy={() => setPrivacyOpen(true)} />
        <PrimeAuditSection />
        <WhatIsAuditSection />
        <WhyAuditSection />
        <PricingSection />
        <WorksSection />
        <ProcessSection />
        <ProvincesSection />
        <FAQSection />
        <CTASection />
      </main>
      <Footer onOpenPrivacy={() => setPrivacyOpen(true)} onOpenTerms={() => setTermsOpen(true)} />
      <MobileCTA showCookies={showCookies} />
      
      {showCookies && <CookieBanner onAccept={() => { setCookieConsent('all'); setShowCookies(false); }} onReject={() => { setCookieConsent('essential'); setShowCookies(false); }} />}
      
      <Dialog open={privacyOpen} onOpenChange={setPrivacyOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] p-0 bg-white" data-testid="privacy-modal">
          <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gray-50"><DialogTitle className="text-xl">Politique de Confidentialité</DialogTitle></DialogHeader>
          <ScrollArea className="h-[60vh] px-6 py-6 bg-white"><PrivacyContent /></ScrollArea>
          <div className="p-4 border-t bg-gray-50"><Button onClick={() => setPrivacyOpen(false)} className="w-full bg-green-600 hover:bg-green-700">Fermer</Button></div>
        </DialogContent>
      </Dialog>
      
      <Dialog open={termsOpen} onOpenChange={setTermsOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] p-0 bg-white" data-testid="terms-modal">
          <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gray-50"><DialogTitle className="text-xl">Conditions Générales et Mentions Légales</DialogTitle></DialogHeader>
          <ScrollArea className="h-[60vh] px-6 py-6 bg-white"><TermsContent /></ScrollArea>
          <div className="p-4 border-t bg-gray-50"><Button onClick={() => setTermsOpen(false)} className="w-full bg-green-600 hover:bg-green-700">Fermer</Button></div>
        </DialogContent>
      </Dialog>
      
      <Toaster position="top-right" richColors />
    </div>
  );
};

// ==================== PAGE CERTIFICAT PEB ====================

const PEB_TARIFS = [
  { type: "Studio", prix: 165 },
  { type: "Appartement < 75m²", prix: 215 },
  { type: "Appartement 75-125m²", prix: 240 },
  { type: "Appartement 126-200m² / Duplex", prix: 265 },
  { type: "Maison < 100m²", prix: 245 },
  { type: "Maison 100-200m²", prix: 265 },
  { type: "Maison 200-300m²", prix: 290 },
  { type: "Maison > 300m²", prix: 340 },
];

const PEB_SCALE = [
  { label: "A", color: "#15803d", range: "≤ 45 kWh/m².an", desc: "Très performant" },
  { label: "B", color: "#22c55e", range: "46-85 kWh/m².an", desc: "Performant" },
  { label: "C", color: "#84cc16", range: "86-170 kWh/m².an", desc: "Moyen" },
  { label: "D", color: "#facc15", range: "171-255 kWh/m².an", desc: "Médiocre" },
  { label: "E", color: "#f97316", range: "256-340 kWh/m².an", desc: "Mauvais" },
  { label: "F", color: "#ef4444", range: "341-425 kWh/m².an", desc: "Très mauvais" },
  { label: "G", color: "#991b1b", range: "> 425 kWh/m².an", desc: "Passoire énergétique" },
];

const SEOHeadPEB = () => (
  <Helmet>
    <title>Certificat PEB Wallonie dès 165€ | Expert Agréé SPW | RDV sous 48h</title>
    <meta name="description" content="Certificat PEB obligatoire pour vente ou location en Wallonie. À partir de 165€ TVAC. Certificateur agréé SPW, RDV sous 48h. Liège, Charleroi, Namur, Mons, Wavre. Devis gratuit." />
    <link rel="canonical" href="https://audits-logement-wallonie.be/certificat-peb" />
    <meta name="keywords" content="certificat PEB, PEB Wallonie, certificat énergétique, PEB Liège, PEB Charleroi, PEB Namur, PEB Mons, PEB obligatoire, vente maison Wallonie, location appartement Belgique, certificateur agréé, performance énergétique bâtiment" />
    <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
    <meta name="geo.region" content="BE-WAL" />
    <meta name="geo.placename" content="Wallonie, Belgique" />
    
    {/* Open Graph */}
    <meta property="og:title" content="Certificat PEB Wallonie dès 165€ | Expert Agréé | RDV 48h" />
    <meta property="og:description" content="Certificat PEB obligatoire pour vente ou location. Auditeurs agréés SPW, RDV sous 48h, intervention dans toute la Wallonie." />
    <meta property="og:url" content="https://audits-logement-wallonie.be/certificat-peb" />
    <meta property="og:type" content="website" />
    <meta property="og:locale" content="fr_BE" />
    <meta property="og:site_name" content="Certificat PEB Wallonie" />
    
    {/* Twitter Card */}
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:title" content="Certificat PEB Wallonie dès 165€" />
    <meta name="twitter:description" content="Auditeur agréé SPW. RDV sous 48h. Devis gratuit." />
    
    {/* JSON-LD Schema */}
    <script type="application/ld+json">{`
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Certificat PEB Wallonie",
        "description": "Service de certification PEB (Performance Énergétique des Bâtiments) par experts agréés en Wallonie. Obligatoire pour vente et location.",
        "provider": {
          "@type": "LocalBusiness",
          "name": "Certificat PEB Wallonie",
          "telephone": "+3278790115",
          "email": "info@renoprimewallonie.be",
          "areaServed": {
            "@type": "AdministrativeArea",
            "name": "Wallonie"
          },
          "priceRange": "165€ - 340€"
        },
        "serviceType": "Certification énergétique PEB",
        "areaServed": ["Liège", "Charleroi", "Namur", "Mons", "Wavre", "Tournai", "Verviers", "Arlon"],
        "offers": {
          "@type": "Offer",
          "price": "165",
          "priceCurrency": "EUR",
          "priceValidUntil": "2026-12-31",
          "availability": "https://schema.org/InStock"
        }
      }
    `}</script>
    <script type="application/ld+json">{`
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Quelle est la durée de validité d'un certificat PEB ?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Un certificat PEB est valable 10 ans à partir de sa date d'émission."
            }
          },
          {
            "@type": "Question",
            "name": "Le certificat PEB est-il obligatoire pour louer ?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Oui, le certificat PEB est obligatoire pour toute mise en location en Wallonie."
            }
          },
          {
            "@type": "Question",
            "name": "Combien coûte un certificat PEB ?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Le prix d'un certificat PEB varie de 165€ pour un studio à 340€ pour une grande maison, tout compris (déplacement, rapport, enregistrement)."
            }
          }
        ]
      }
    `}</script>
  </Helmet>
);

const HeaderPEB = () => (
  <header className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm">
    <nav className="container mx-auto px-3 lg:px-8">
      <div className="flex items-center justify-between h-14 lg:h-16">
        <a href="/certificat-peb" className="flex items-center gap-1.5 lg:gap-2">
          <img src="/logo.svg" alt="" className="w-7 h-7 lg:w-10 lg:h-10" />
          <div>
            <span className="font-bold text-sm lg:text-lg leading-tight block text-gray-900">Certificat PEB</span>
            <span className="text-[10px] lg:text-xs font-semibold tracking-wide text-green-600">WALLONIE</span>
          </div>
        </a>
        
        {/* Navigation Menu */}
        <div className="hidden lg:flex items-center gap-6">
          <a href="/" className="text-gray-700 hover:text-green-600 font-medium text-sm transition-colors">Audit Logement</a>
          <a href="/certificat-peb" className="text-green-600 font-medium text-sm">Certificat PEB</a>
          <a href="/simulateur" className="text-gray-700 hover:text-green-600 font-medium text-sm transition-colors">Simulateur Rénopack</a>
        </div>
        
        {/* Phone icon only */}
        <a href={`tel:${PHONE_LINK}`} onClick={() => trackPhoneClick('Header PEB')} className="flex items-center justify-center w-10 h-10 bg-green-50 text-green-700 rounded-full hover:bg-green-100 transition-colors">
          <Phone className="w-5 h-5" />
        </a>
      </div>
    </nav>
  </header>
);

const CertificatPEBPage = () => {
  usePageTracking(); // Track pageview on route change
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCookies, setShowCookies] = useState(true);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [form, setForm] = useState({ civilite: '', nom: '', prenom: '', email: '', telephone: '', code_postal: '', type_bien: '', message: '', consent_rgpd: false });

  useEffect(() => {
    if (localStorage.getItem('cookieConsent')) setShowCookies(false);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.telephone) { toast.error("Veuillez renseigner votre email et téléphone"); return; }
    if (!form.consent_rgpd) { toast.error("Veuillez accepter la politique de confidentialité"); return; }
    
    setIsSubmitting(true);
    try {
      const utmParams = getUtmParams();
      const current_page = window.location.pathname;
      const res = await axios.post(`${API}/contact`, { 
        ...form, 
        ...utmParams,
        source: 'peb',
        form_type: 'peb',
        current_page,
        landing_url: utmParams.landing_url || window.location.href,
        services_supplementaires: 'Certificat PEB'
      });
      if (res.data.success) {
        trackFormSubmit('peb');
        toast.success("Demande envoyée !", { description: `Un expert PEB vous contactera sous 24h.`, duration: 5000 });
        setForm({ civilite: '', nom: '', prenom: '', email: '', telephone: '', code_postal: '', type_bien: '', message: '', consent_rgpd: false });
      }
    } catch { toast.error("Erreur", { description: `Veuillez réessayer ou appeler le ${PHONE_DISPLAY}` }); }
    finally { setIsSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="peb-page">
      <SEOHeadPEB />
      <RegulationBanner />
      <HeaderPEB />
      
      <main>
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-green-900 via-green-800 to-green-900 text-white">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}} />
          </div>
          
          <div className="container mx-auto px-4 lg:px-8 py-12 lg:py-20 relative">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Left content */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <img src="/images/logo-wallonie-small.png" alt="Région Wallonne" className="h-10 w-auto" />
                  <span className="text-green-300 text-sm font-medium">Auditeurs agréés SPW</span>
                </div>
                
                <h1 className="text-3xl lg:text-5xl font-bold mb-4 leading-tight">
                  Votre Certificat PEB<br/>
                  <span className="text-yellow-400">dès 165€</span> en 48h
                </h1>
                
                <p className="text-lg text-green-100 mb-6 leading-relaxed">
                  Obligatoire pour toute <strong className="text-white">vente ou location</strong> en Wallonie. 
                  Nos certificateurs agréés interviennent rapidement partout en Wallonie.
                </p>
                
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {[
                    { icon: Clock, text: "RDV sous 48h" },
                    { icon: ShieldCheck, text: "Auditeur agréé" },
                    { icon: Euro, text: "Dès 165€ TVAC" },
                    { icon: MapPin, text: "Toute Wallonie" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <item.icon className="w-4 h-4 text-green-400" />
                      <span>{item.text}</span>
                    </div>
                  ))}
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button onClick={() => document.getElementById('form-peb')?.scrollIntoView({ behavior: 'smooth' })} className="flex-1 sm:flex-initial bg-green-500 hover:bg-green-600 text-white font-semibold px-8 py-3.5 rounded-full text-base h-auto shadow-xl shadow-green-500/30">
                    Contacter un certificateur
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                  <a href={`tel:${PHONE_LINK}`} onClick={() => trackPhoneClick('Hero PEB')} className="flex-1 sm:flex-initial flex items-center justify-center gap-2 border-2 border-white/30 text-white px-6 py-3 rounded-full font-medium hover:bg-white/10 transition-colors text-sm">
                    <Phone className="w-4 h-4" />
                    {PHONE_DISPLAY}
                  </a>
                </div>
              </div>
              
              {/* Right: PEB Scale visual */}
              <div className="hidden lg:block">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <h3 className="text-lg font-semibold mb-4 text-center">Échelle de performance énergétique</h3>
                  <div className="space-y-2">
                    {PEB_SCALE.map((item) => (
                      <div key={item.label} className="flex items-center gap-3">
                        <div className="w-10 h-8 rounded flex items-center justify-center font-bold text-white text-lg" style={{ backgroundColor: item.color }}>
                          {item.label}
                        </div>
                        <div className="flex-1 text-sm">
                          <span className="text-white/90">{item.range}</span>
                          <span className="text-white/60 ml-2">• {item.desc}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Banner */}
        <section className="bg-gray-50 border-b py-4">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap justify-center gap-6 lg:gap-12 text-sm text-gray-600">
              {[
                { icon: Clock, text: "RDV sous 48h garantie" },
                { icon: ShieldCheck, text: "Certificateurs agréés SPW" },
                { icon: Star, text: "Validité 10 ans" },
                { icon: FileCheck, text: "Enregistrement officiel" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <item.icon className="w-4 h-4 text-green-600" />
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Qu'est-ce que le PEB */}
        <section className="py-16 lg:py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">Qu'est-ce que le Certificat PEB ?</h2>
              <p className="text-gray-600 text-lg">Le certificat de Performance Énergétique des Bâtiments évalue la consommation énergétique de votre bien immobilier.</p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
              {[
                { icon: FileText, title: "Document officiel", desc: "Délivré par un certificateur agréé par la Région Wallonne. Enregistré dans la base de données officielle." },
                { icon: Thermometer, title: "Performance énergétique", desc: "Classe votre bien de A (très performant) à G (passoire énergétique) selon sa consommation en kWh/m².an." },
                { icon: Shield, title: "Obligation légale", desc: "Obligatoire pour toute vente ou mise en location. Doit figurer dans les annonces immobilières." },
              ].map((item, i) => (
                <div key={i} className="bg-white border rounded-xl p-6 hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-lg text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pourquoi le PEB est obligatoire */}
        <section className="py-16 lg:py-20 bg-green-50">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div>
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-6">Pourquoi le PEB est-il obligatoire ?</h2>
                
                <div className="space-y-4">
                  {[
                    { title: "Vente immobilière", desc: "Tout vendeur doit fournir un certificat PEB valide avant la mise en vente. Il doit être mentionné dans les annonces." },
                    { title: "Location", desc: "Les propriétaires bailleurs doivent présenter le certificat PEB aux locataires potentiels avant la signature du bail." },
                    { title: "Transparence énergétique", desc: "Permet aux acheteurs et locataires de comparer les biens et d'anticiper leurs charges énergétiques." },
                    { title: "Valeur du bien", desc: "Un bon score PEB (A, B, C) peut augmenter la valeur de votre bien de 5 à 15% et accélérer sa vente." },
                  ].map((item, i) => (
                    <div key={i} className="flex gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-gray-900">{item.title}</h4>
                        <p className="text-gray-600 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="font-semibold text-lg text-gray-900 mb-4 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-500" />
                  Sanctions en cas de non-conformité
                </h3>
                <ul className="space-y-3 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 font-bold">•</span>
                    Amende de 500€ à 50.000€ pour absence de PEB lors d'une vente
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 font-bold">•</span>
                    Annulation possible de la transaction immobilière
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 font-bold">•</span>
                    Obligation d'afficher le score PEB dans toutes les annonces
                  </li>
                </ul>
                <div className="mt-4 p-3 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-800"><strong>Validité :</strong> Un certificat PEB est valable 10 ans, sauf si des travaux de rénovation énergétique ont été réalisés.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Processus en 4 étapes */}
        <section className="py-16 lg:py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">Votre certificat PEB en 4 étapes</h2>
              <p className="text-gray-600">Un processus simple et rapide</p>
            </div>
            
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { step: "1", icon: FileText, title: "Demande", desc: "Remplissez le formulaire ou appelez-nous" },
                { step: "2", icon: Calendar, title: "Rendez-vous", desc: "Un expert vous contacte sous 24h pour planifier" },
                { step: "3", icon: ClipboardCheck, title: "Visite", desc: "Expertise sur site par certificateur agréé" },
                { step: "4", icon: FileCheck, title: "Certificat", desc: "Réception de votre PEB officiel en 48h" },
              ].map((item, i) => (
                <div key={i} className="relative text-center">
                  <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                    {item.step}
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                  {i < 3 && <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-green-200" />}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Tarifs */}
        <section className="py-16 lg:py-20 bg-gray-50">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">Tarifs Certificat PEB</h2>
              <p className="text-gray-600">Prix transparents, tout compris (déplacement, rapport, enregistrement)</p>
            </div>
            
            <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="bg-green-600 text-white px-6 py-4">
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Type de bien</span>
                  <span className="font-semibold">Prix TVAC</span>
                </div>
              </div>
              <div className="divide-y">
                {PEB_TARIFS.map((item, i) => (
                  <div key={i} className={`px-6 py-4 flex justify-between items-center ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <span className="text-gray-700">{item.type}</span>
                    <span className="font-bold text-green-600 text-lg">{item.prix}€</span>
                  </div>
                ))}
              </div>
              <div className="bg-green-50 px-6 py-4">
                <div className="flex flex-wrap gap-4 text-sm text-green-800">
                  <span className="flex items-center gap-1"><Check className="w-4 h-4" /> Déplacement inclus</span>
                  <span className="flex items-center gap-1"><Check className="w-4 h-4" /> Rapport complet</span>
                  <span className="flex items-center gap-1"><Check className="w-4 h-4" /> RDV sous 48h</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Zones d'intervention */}
        <section className="py-16 lg:py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">Nos zones d'intervention en Wallonie</h2>
              <p className="text-gray-600">Nos certificateurs PEB agréés interviennent dans toutes les provinces wallonnes</p>
            </div>
            
            <div className="grid md:grid-cols-5 gap-4 lg:gap-6 mb-10">
              {[
                { province: "Liège", villes: ["Liège", "Verviers", "Seraing", "Herstal", "Visé", "Waremme"] },
                { province: "Hainaut", villes: ["Charleroi", "Mons", "La Louvière", "Tournai", "Mouscron", "Binche"] },
                { province: "Namur", villes: ["Namur", "Dinant", "Sambreville", "Gembloux", "Andenne", "Ciney"] },
                { province: "Brabant wallon", villes: ["Wavre", "Nivelles", "Ottignies-LLN", "Braine-l'Alleud", "Tubize", "Jodoigne"] },
                { province: "Luxembourg", villes: ["Arlon", "Marche-en-Famenne", "Bastogne", "Virton", "Aubange", "Libramont"] },
              ].map((zone, i) => (
                <div key={i} className="bg-white border rounded-xl p-5 hover:shadow-lg transition-shadow">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="w-5 h-5 text-green-600" />
                    <h3 className="font-bold text-gray-900">{zone.province}</h3>
                  </div>
                  <ul className="space-y-1">
                    {zone.villes.map((ville, j) => (
                      <li key={j} className="text-sm text-gray-600 flex items-center gap-1.5">
                        <span className="w-1 h-1 bg-green-500 rounded-full"></span>
                        PEB {ville}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            
            <div className="bg-green-50 rounded-2xl p-6 lg:p-8">
              <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <img src="/images/logos/logo-wallonie-official.png" alt="Région Wallonne" className="h-14 w-auto" />
                  <div>
                    <h4 className="font-bold text-gray-900 text-lg">Toute la Wallonie couverte</h4>
                    <p className="text-gray-600 text-sm">262 communes • 5 provinces • Intervention rapide garantie</p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button onClick={() => document.getElementById('form-peb')?.scrollIntoView({ behavior: 'smooth' })} className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-full font-semibold">
                    Contacter un certificateur
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Lien vers Audit Logement */}
        <section className="py-12 bg-gradient-to-r from-green-700 to-green-800 text-white">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-xl lg:text-2xl font-bold mb-2">Besoin d'un Audit Logement ?</h3>
                <p className="text-green-100">Requis pour accéder au Rénopack : prêt à taux 0% et jusqu'à 50% de réduction. L'audit est finançable dans le prêt.</p>
              </div>
              <a href="/" className="flex items-center gap-2 bg-white text-green-700 px-6 py-3 rounded-full font-semibold hover:bg-green-50 transition-colors whitespace-nowrap">
                Découvrir l'Audit Logement
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </section>

        {/* Formulaire */}
        <section id="form-peb" className="py-16 lg:py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">Contacter un certificateur agréé</h2>
                <p className="text-gray-600">Un certificateur PEB agréé vous rappelle sous 24h</p>
              </div>
              
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-6 lg:p-8 border">
                {/* Type de bien avec prix - EN PREMIER */}
                <div className="mb-6">
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">Type de bien *</Label>
                  <div className="grid grid-cols-1 gap-2">
                    {[
                      { value: "Studio", prix: "165€", desc: "Surface < 50m²" },
                      { value: "Appartement", prix: "215-265€", desc: "Selon surface" },
                      { value: "Maison", prix: "245-340€", desc: "Selon surface" },
                      { value: "Duplex", prix: "265€", desc: "2 niveaux" },
                      { value: "Immeuble", prix: "Sur devis", desc: "Multi-logements" },
                    ].map((item) => (
                      <label
                        key={item.value}
                        className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-all ${
                          form.type_bien === item.value 
                            ? 'border-green-500 bg-green-50 ring-2 ring-green-500/20' 
                            : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="radio"
                            name="type_bien"
                            value={item.value}
                            checked={form.type_bien === item.value}
                            onChange={(e) => setForm({...form, type_bien: e.target.value})}
                            className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                          />
                          <div>
                            <span className="font-medium text-gray-900">{item.value}</span>
                            <span className="text-gray-500 text-sm ml-2">• {item.desc}</span>
                          </div>
                        </div>
                        <span className="font-semibold text-green-600">{item.prix}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">* Prix indicatifs TVAC</p>
                </div>
                
                {/* Civilité */}
                <div className="mb-3">
                  <Label className="text-sm font-medium text-gray-700 mb-1.5 block">Civilité <span className="text-gray-400 font-normal">(optionnel)</span></Label>
                  <div className="flex gap-2">
                    {[{value:'M', label:'Monsieur'}, {value:'Mme', label:'Madame'}].map(opt => (
                      <label 
                        key={opt.value}
                        className={`cursor-pointer px-4 py-1.5 rounded-full border text-sm transition-all ${
                          form.civilite === opt.value 
                            ? 'border-green-500 bg-green-50 text-green-700 font-medium' 
                            : 'border-gray-200 text-gray-600 hover:border-green-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="civilite-peb"
                          value={opt.value}
                          checked={form.civilite === opt.value}
                          onChange={() => setForm({...form, civilite: form.civilite === opt.value ? '' : opt.value})}
                          className="sr-only"
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label htmlFor="prenom" className="text-sm font-medium text-gray-700">Prénom</Label>
                    <Input id="prenom" value={form.prenom} onChange={e => setForm({...form, prenom: e.target.value})} placeholder="Jean" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="nom" className="text-sm font-medium text-gray-700">Nom</Label>
                    <Input id="nom" value={form.nom} onChange={e => setForm({...form, nom: e.target.value})} placeholder="Dupont" className="mt-1" />
                  </div>
                </div>
                
                <div className="mb-4">
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email *</Label>
                  <Input id="email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="jean.dupont@email.be" className="mt-1" required />
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label htmlFor="telephone" className="text-sm font-medium text-gray-700">Téléphone *</Label>
                    <Input id="telephone" type="tel" value={form.telephone} onChange={e => setForm({...form, telephone: e.target.value})} placeholder="+32 4XX XX XX XX" className="mt-1" required />
                  </div>
                  <div>
                    <Label htmlFor="code_postal" className="text-sm font-medium text-gray-700">Code postal</Label>
                    <Input id="code_postal" value={form.code_postal} onChange={e => setForm({...form, code_postal: e.target.value})} placeholder="4000" className="mt-1" />
                  </div>
                </div>
                
                <div className="mb-6">
                  <Label htmlFor="message" className="text-sm font-medium text-gray-700">Message (optionnel)</Label>
                  <textarea id="message" value={form.message} onChange={e => setForm({...form, message: e.target.value})} placeholder="Précisions sur votre bien..." className="mt-1 w-full px-3 py-2 border rounded-lg text-sm resize-none h-20" />
                </div>
                
                <div className="flex items-start gap-3 mb-6">
                  <Checkbox id="rgpd-peb" checked={form.consent_rgpd} onCheckedChange={c => setForm({...form, consent_rgpd: c})} className="h-5 w-5 mt-0.5 border-2 border-gray-300 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600 cursor-pointer flex-shrink-0" data-testid="checkbox-rgpd-peb" />
                  <Label htmlFor="rgpd-peb" className="text-sm text-gray-600 cursor-pointer leading-tight">
                    J'accepte d'être recontacté(e) et la <button type="button" onClick={() => setPrivacyOpen(true)} className="text-green-600 underline">politique de confidentialité</button>
                  </Label>
                </div>
                <div className="-mt-3 mb-6">
                  <AdsConsentCheckbox id="ads-consent-peb" checked={form.consent_google_ads} onChange={(v) => setForm({...form, consent_google_ads: v})} />
                </div>
                
                <Button type="submit" disabled={isSubmitting} className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-full text-lg">
                  {isSubmitting ? "Envoi en cours..." : "Recevoir mon devis gratuit"}
                </Button>
                
                <p className="text-center text-xs text-gray-500 mt-4">Service 100% gratuit et sans engagement</p>
              </form>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 lg:py-20 bg-gray-50">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">Questions fréquentes</h2>
              </div>
              
              <Accordion type="single" collapsible className="space-y-3">
                {[
                  { q: "Quelle est la durée de validité d'un certificat PEB ?", a: "Un certificat PEB est valable 10 ans à partir de sa date d'émission. Cependant, si vous réalisez des travaux de rénovation énergétique importants (isolation, nouveau système de chauffage...), il est recommandé de faire établir un nouveau certificat pour refléter l'amélioration de la performance de votre bien." },
                  { q: "Le certificat PEB est-il obligatoire pour louer ?", a: "Oui, le certificat PEB est obligatoire pour toute mise en location en Wallonie. Le propriétaire doit le présenter aux locataires potentiels et le score énergétique doit figurer dans les annonces immobilières." },
                  { q: "Combien de temps dure une visite pour établir un PEB ?", a: "La visite d'un certificateur agréé dure généralement entre 30 minutes et 1h30 selon la taille et la complexité du bien. L'expert relève les caractéristiques énergétiques : isolation, chauffage, ventilation, etc." },
                  { q: "Quelle est la différence entre PEB et Audit Logement ?", a: "Le certificat PEB évalue la performance énergétique actuelle de votre bien (note A à G). L'Audit Logement est une analyse plus complète qui identifie les travaux prioritaires et est requis pour obtenir le Rénopack ou le Rénoprêt en Wallonie." },
                  { q: "Comment améliorer mon score PEB ?", a: "Pour améliorer votre score PEB, vous pouvez : isoler la toiture et les murs, remplacer les anciens châssis par du double/triple vitrage, installer un système de chauffage performant (pompe à chaleur, chaudière à condensation), ou améliorer la ventilation." },
                ].map((item, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="bg-white rounded-xl border px-6">
                    <AccordionTrigger className="text-left font-medium text-gray-900 py-4 hover:no-underline">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 pb-4">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-12 bg-green-600 text-white">
          <div className="container mx-auto px-4 lg:px-8 text-center">
            <h2 className="text-2xl lg:text-3xl font-bold mb-4">Prêt pour votre Certificat PEB ?</h2>
            <p className="text-green-100 mb-6">Auditeurs agréés • RDV sous 48h • Dès 165€ TVAC</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => document.getElementById('form-peb')?.scrollIntoView({ behavior: 'smooth' })} className="bg-yellow-500 hover:bg-yellow-400 text-green-900 font-semibold px-8 py-3.5 rounded-full text-base h-auto min-w-[200px]">
                Contacter un certificateur
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <a href={`tel:${PHONE_LINK}`} onClick={() => trackPhoneClick('CTA Final PEB')} className="flex items-center justify-center gap-2 border-2 border-white/40 text-white px-6 py-3 rounded-full font-medium hover:bg-white/10 transition-colors text-sm">
                <Phone className="w-4 h-4" />
                ou appelez le {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* Footer - Same as main page */}
      <footer className="bg-white border-t border-gray-200 py-12">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <Logo />
              <p className="text-gray-500 mt-4 text-sm max-w-md leading-relaxed">
                Service gratuit de mise en relation avec des auditeurs agréés par la Région Wallonne. 
                Bénéficiez du Rénopack (prêt à taux 0%) pour votre audit logement et vos travaux de rénovation énergétique.
              </p>
              <a href={`tel:${PHONE_LINK}`} onClick={() => trackPhoneClick('Footer')} className="inline-flex items-center gap-2 text-green-600 font-semibold mt-4 hover:text-green-700">
                <Phone className="w-4 h-4" />{PHONE_DISPLAY}
              </a>
            </div>
            
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Nos services</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><a href="/" className="hover:text-green-600">Audit Logement</a></li>
                <li><a href="/certificat-peb" className="hover:text-green-600">Certificat PEB</a></li>
                <li><a href="/simulateur" className="hover:text-green-600">Simulateur Rénopack</a></li>
                <li><a href="#formulaire-peb" className="hover:text-green-600">Demander un devis</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Légal</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><button onClick={() => setPrivacyOpen(true)} className="hover:text-green-600">Politique de confidentialité</button></li>
                <li><button onClick={() => setTermsOpen(true)} className="hover:text-green-600">Conditions générales</button></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-100 pt-8 text-center text-gray-400 text-xs">
            © {new Date().getFullYear()} Audits Logement Wallonie · Édité par Ulysse Julien Berneau · NIE Y5618264E · Service de mise en relation gratuit
          </div>
        </div>
      </footer>

      {/* Cookie Banner */}
      {showCookies && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-50">
          <div className="container mx-auto flex items-center justify-between gap-4">
            <p className="text-xs text-gray-600 flex items-center gap-2">
              <Cookie className="w-4 h-4 text-amber-500" />
              Cookies de mesure Google Ads, seulement si vous acceptez.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => { setCookieConsent('essential'); setShowCookies(false); }} data-testid="peb-cookie-reject">Refuser</Button>
              <Button size="sm" className="bg-green-600" onClick={() => { setCookieConsent('all'); setShowCookies(false); }} data-testid="peb-cookie-accept">Accepter</Button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Floating CTA */}
      <div className="lg:hidden fixed bottom-4 left-4 right-4 z-40" style={{ marginBottom: showCookies ? '84px' : '0' }}>
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-3">
          <Button onClick={() => document.getElementById('form-peb')?.scrollIntoView({ behavior: 'smooth' })} className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl font-semibold text-sm h-auto">
            Contacter un certificateur
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* Privacy Modal */}
      <Dialog open={privacyOpen} onOpenChange={setPrivacyOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] p-0 bg-white">
          <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gray-50"><DialogTitle className="text-xl">Politique de Confidentialité</DialogTitle></DialogHeader>
          <ScrollArea className="h-[60vh] px-6 py-6 bg-white"><PrivacyContent /></ScrollArea>
          <div className="p-4 border-t bg-gray-50"><Button onClick={() => setPrivacyOpen(false)} className="w-full bg-green-600 hover:bg-green-700">Fermer</Button></div>
        </DialogContent>
      </Dialog>

      {/* Terms Modal */}
      <Dialog open={termsOpen} onOpenChange={setTermsOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] p-0 bg-white">
          <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gray-50"><DialogTitle className="text-xl">Conditions Générales</DialogTitle></DialogHeader>
          <ScrollArea className="h-[60vh] px-6 py-6 bg-white"><TermsContent /></ScrollArea>
          <div className="p-4 border-t bg-gray-50"><Button onClick={() => setTermsOpen(false)} className="w-full bg-green-600 hover:bg-green-700">Fermer</Button></div>
        </DialogContent>
      </Dialog>
      
      <Toaster position="top-right" richColors />
    </div>
  );
};

// ============================================
// PRIVACY PAGE - Page dédiée confidentialité
// ============================================
const PrivacyPage = () => {
  usePageTracking(); // Track pageview on route change

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Politique de Confidentialité | Audits Logement Wallonie</title>
        <meta name="description" content="Politique de confidentialité et protection des données personnelles - Audits Logement Wallonie" />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img src="/images/logo-wallonie-small.png" alt="Région Wallonne" className="h-10 w-auto" />
            <span className="text-lg font-bold text-gray-900">Audits Logement Wallonie</span>
          </a>
          <a href="/" className="text-green-600 hover:text-green-700 font-medium text-sm">← Retour à l'accueil</a>
        </div>
      </header>
      
      <main className="container mx-auto px-4 py-12 max-w-3xl">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <PrivacyContent />
          
          <div className="mt-8 pt-6 border-t border-gray-200">
            <TermsContent />
          </div>
        </div>
        
        <div className="mt-8 text-center">
          <a href="/" className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 font-medium">
            ← Retour à l'accueil
          </a>
        </div>
      </main>
      
      <footer className="bg-gray-900 text-white py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-gray-400">
          © 2026 Audits Logement Wallonie — Service de mise en relation gratuit
        </div>
      </footer>
    </div>
  );
};

// Old primes estimator retired (primes ended 30/09/2026): keep ad params (gclid, keyword)
const RedirectToSimulator = () => {
  const { search } = useLocation();
  return <Navigate to={{ pathname: "/simulateur", search }} replace />;
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/wallonie" element={<LandingPage />} />
        <Route path="/simulateur" element={<SimulatorRenopack />} />
        <Route path="/estimation" element={<RedirectToSimulator />} />
        <Route path="/certificat-peb" element={<CertificatPEBPage />} />
        <Route path="/confidentialite" element={<PrivacyPage />} />
        <Route path="/admin/leads" element={<AdminLeads />} />
        <Route path="/privacy" element={<PrivacyPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
